// @ts-nocheck
import { createClient } from "@supabase/supabase-js";
import { rowFingerprint, changedRows, upsertSlices, pruneIds, idSetKey } from "./syncdiff";
import { keyModesFrom, conflictTarget, isConflictSpecError, flipMode } from "./synckeys";
import { stockMatch } from "./stockmatch";   // PO receiving finds the item by SKU first (§13 item 26)

/* ============================================================
   Fence Works — Supabase cloud sync (window.VFP_DB)
   Reads / writes a normalized schema (workspaces, products,
   projects, quotes, activity) scoped to one workspace.
   Each row keeps a lossless `data` blob so the app round-trips
   exactly what it saved. Browser-safe: PUBLISHABLE key + RLS.
   ============================================================ */
(function () {
  "use strict";

  var SUPA_URL = "https://qzeprxybirfnrcnvofyx.supabase.co";
  var SUPA_KEY = "sb_publishable_dKxxMBuSzbewb4QfV_1o5Q_nBoTi3CF";
  var WS = "fenceworks";   // resolved per logged-in user below; default for local/public
  var DEFAULT_WS = "fenceworks";

  // session bearer token for app → service calls (the service verifies it and
  // matches the workspace — see server/src/auth.js)
  async function authHeaders() {
    try {
      var s = await sb.auth.getSession();
      var tok = s && s.data && s.data.session && s.data.session.access_token;
      return tok ? { Authorization: "Bearer " + tok } : {};
    } catch (e) { return {}; }
  }

  // ONE 401 handler for every app → service call. An expired access token gets
  // a single refreshSession() + retry with fresh headers; if the service still
  // answers 401 the session is really gone, so tell the pill ("Signed out —
  // sign in again") instead of letting each caller surface a bare "401".
  async function svcFetch(url, init) {
    init = init || {};
    var r = await fetch(url, init);
    // A plan gate on the service ("403 {error:'plan'}") means our idea of the
    // plan is stale (card failed, subscription canceled) — re-sync it now so
    // the screens lock without waiting for a reload.
    if (r.status === 403) {
      try { r.clone().json().then(function (j) { if (j && j.error === "plan" && window.VFP_RESYNC_PLAN) window.VFP_RESYNC_PLAN(); }).catch(function () {}); } catch (e) {}
      return r;
    }
    if (r.status !== 401 || !sb) return r;
    var refreshed = false;
    try {
      var rs = await sb.auth.refreshSession();
      refreshed = !!(rs && rs.data && rs.data.session);
    } catch (e) {}
    if (refreshed) {
      var h = Object.assign({}, init.headers || {}, await authHeaders());
      r = await fetch(url, Object.assign({}, init, { headers: h }));
      if (r.status !== 401) return r;
    }
    emit("nosession");
    return r;
  }

  // A refused billing call carries a message written for the owner ("Remove 2
  // team members first…") and a code the screen can branch on. The old
  // "checkout 409: {json}" string showed the raw body instead.
  async function billingError(r, what) {
    var text = await r.text().catch(function () { return ""; });
    var d = {};
    try { d = JSON.parse(text) || {}; } catch (e) {}
    var e = new Error(d.message || d.error || (what + " " + r.status + (text ? ": " + text : "")));
    e.code = d.message ? (d.error || null) : null;
    e.status = r.status;
    e.detail = d;
    return e;
  }

  // The signup trigger (handle_new_user) names each workspace deterministically:
  // ws_<auth uid without dashes>. Mirror that so we can resolve a brand-new
  // account's workspace even before its users row is readable.
  function deriveWs(uid) { return uid ? ("ws_" + String(uid).replace(/-/g, "")) : null; }

  // Multi-tenant: each user is tied to one workspace via public.users.workspace_id.
  // Resolve it for the current session before any read/write. Order:
  //   1. users.workspace_id  (authoritative; RLS lets a user read their own row)
  //   2. memberships          (back-compat)
  //   3. derive ws_<uid>      (a brand-new signup whose row isn't readable yet)
  //   4. DEFAULT_WS           (only when there's NO session — local/public)
  // Critically, an AUTHENTICATED user never silently falls back to the shared
  // default workspace (that was showing every new signup the demo data).
  // Memoized per signed-in user: every DB method calls this before touching a
  // table, and on first paint a dozen call in parallel — without the memo each
  // one re-ran the users/memberships lookups (the repeated `users?select=`
  // queries visible in the network tab). getSession() is local (no network),
  // so checking the uid each call is free; a sign-out/in re-resolves.
  var wsResolvedUid;          // uid (or null for signed-out) WS is resolved for
  var wsInflight = null;      // in-flight resolution promise (dedupes the burst)
  async function resolveWorkspace() {
    if (!sb) return WS;
    if (demoActive()) { WS = demoWs(); return WS; }
    var uid = null;
    try {
      var s = await sb.auth.getSession();
      uid = (s && s.data && s.data.session && s.data.session.user && s.data.session.user.id) || null;
    } catch (e) { return WS; }
    if (wsResolvedUid !== undefined && wsResolvedUid === uid) return wsInflight || WS;
    wsResolvedUid = uid;
    wsInflight = (async function () {
      try {
        if (!uid) { WS = DEFAULT_WS; return WS; }
        var r = await sb.from("users").select("workspace_id").eq("id", uid).maybeSingle();
        if (r && r.data && r.data.workspace_id) { WS = r.data.workspace_id; return WS; }
        var m = await sb.from("memberships").select("workspace_id").eq("user_id", uid).order("created_at", { ascending: true }).limit(1);
        if (m && m.data && m.data[0] && m.data[0].workspace_id) { WS = m.data[0].workspace_id; return WS; }
        WS = deriveWs(uid) || DEFAULT_WS;   // signed in but no row yet → their own ws, never the shared default
      } catch (e) {
        wsResolvedUid = undefined;          // transient failure → retry on the next call
        // NEVER leave a signed-in user on the shared default workspace: a
        // lookup that threw used to keep WS = "fenceworks", so their next
        // load/save touched the demo tenant. Their own derived ws (or null,
        // which load()/persist() refuse) is the only safe answer here.
        WS = deriveWs(uid) || null;
      }
      return WS;
    })();
    var out = await wsInflight;
    wsInflight = null;
    return out;
  }

  // bundled @supabase/supabase-js (was the UMD CDN global window.supabase)
  var ready = true;
  var sb = ready ? createClient(SUPA_URL, SUPA_KEY, { auth: { persistSession: true, autoRefreshToken: true }, realtime: { params: { eventsPerSecond: 2 } } }) : null;

  // ---------- demo mode (sandbox) ----------
  // When window.VFP_DEMO.active, this module must never touch Supabase: no
  // load, no save, no realtime, no typed-table reads — even if a real session
  // is still cached in this browser. The store runs entirely off its own
  // localStorage key (see lib/demo.ts); every method below checks demoActive().
  function demoActive() { var d = window.VFP_DEMO; return !!(d && d.active); }
  function demoWs() { var d = window.VFP_DEMO; return "demo_" + ((d && d.sessionId) || "local"); }

  var listeners = [];
  function emit(s, d) { listeners.forEach(function (fn) { try { fn(s, d); } catch (e) {} }); }
  function fail(e) { emit("error", e && (e.message || e.code || String(e))); throw e; }

  /* the heads-up before Google's unverified-app screen (oauthStart) */
  var GOOGLE_USES = {
    gcal: "add and update your job appointments on your Google Calendar. It only touches events, never your calendar settings or who you share with.",
    gsheets: "add a row to the Google Sheet you choose each time a new lead comes in.",
    gbp: "read your Business Profile, reviews and performance numbers, and post the replies and updates you approve. Nothing changes on your profile unless you press the button for it.",
    gmail: "send the emails you write in Fence Works from your own Gmail address. It can send only; it can't read your inbox.",
  };
  function googleHeadsUp(provider) {
    return {
      title: "Heads up: Google will show a warning",
      body: "Google is still reviewing Fence Works (every new app goes through this), so the next screen says \u201cGoogle hasn\u2019t verified this app.\u201d\n\n"
        + "To continue: click \u201cAdvanced\u201d, then \u201cGo to fenceworks.ai\u201d.\n\n"
        + "Fence Works will use this connection only to " + (GOOGLE_USES[provider] || "run the feature you're turning on.") + " You can disconnect any time in Integrations.",
      confirmLabel: "Continue to Google",
      cancelLabel: "Not now",
    };
  }

  // ---------- helpers ----------
  function num(v) { return (v === undefined || v === null || v === "") ? null : Number(v); }

  function inList(ids) {
    // build a PostgREST in() value: ("a","b","c")
    return "(" + ids.map(function (i) { return '"' + String(i).replace(/"/g, '') + '"'; }).join(",") + ")";
  }
  function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "x"; }

  // ---------- AI / integrations / sequences: typed-table data layer ----------
  // These four areas used to live in workspaces.settings.ai (jsonb). They now
  // persist to typed tables (ai_settings, ai_automations, integrations,
  // sequences) keyed by the canonical automation id from automations.js, while
  // still hydrating back into the settings shape the UI already reads.

  // canonical def id (ai_automations.automation_id) <-> UI card id, from the
  // catalog the AI screen registers on window at runtime.
  function aiCatalog() { return window.AI_AUTOMATIONS || []; }
  function defToCard() { var m = {}; aiCatalog().forEach(function (a) { m[a.def] = a.id; }); return m; }
  function defaultTpl(card) {
    var f = window.VFP_AI && window.VFP_AI.defaultTemplate;
    return f ? f(card) : "Hi {customer}, this is {company} — {link}";
  }
  // secret fields that must NEVER be written to the integrations table (they go
  // to Vault via the automation service instead).
  var INTEG_SECRETS = { twilio: ["authToken"], retell: ["apiKey"], resend: ["apiKey"], crm: ["apiKey"], jobber: ["apiKey"], wisetack: ["apiKey"], stripe: ["secretKey"] };

  // did the workspace have ANY automation rows when this tab loaded it? false =
  // a brand-new workspace, whose first saves write each card's `newCfg` (a
  // setting that is on for new accounts and off for existing ones, like the
  // quote follow-up's "before it expires" reminder). null = never loaded.
  var aiRowsAtLoad: boolean | null = null;
  // read the four typed tables → the settings.{ai,integrations,sequences} shape.
  // `pre` (optional) supplies already-fetched rows (from the one-shot RPC in
  // load()) so we transform them with the SAME logic instead of re-querying.
  async function loadAI(pre?: any) {
    var res = { ai: {}, integrations: {}, sequences: [], hasAI: false, hasInteg: false, hasSeq: false };
    if (!sb) return res;
    try {
      var aisData = pre ? (pre.ai_settings || null) : (await sb.from("ai_settings").select("*").eq("workspace_id", WS).maybeSingle()).data;
      var aaData = pre ? (pre.ai_automations || []) : (await sb.from("ai_automations").select("*").eq("workspace_id", WS)).data;
      aiRowsAtLoad = !!(aaData && aaData.length);   // none → a new workspace: cards' newCfg applies (persistAI)
      var d2c = defToCard();
      var ai = {};
      if (aisData) {
        var r = aisData;
        ai.receptionist = {
          enabled: !!r.receptionist_on, voiceEngine: r.voice_engine || "s2s_mini", voice: r.agent_voice || "warm-f",
          mode: r.answer_mode || "afterhours", greeting: r.greeting || "", number: (r.data && r.data.forwardNumber) || "",
          canBook: r.can_book !== false, textLink: r.text_link !== false,
          retellAgentId: r.retell_agent_id || null, trackedNumber: r.tracked_number || null,
        };
        ai.routing = r.routing || {}; ai.billing = r.billing || {};
        res.hasAI = true;
      }
      if (aaData && aaData.length) {
        ai.automations = {}; ai.automationCfg = {};
        aaData.forEach(function (row) { var card = d2c[row.automation_id] || row.automation_id; ai.automations[card] = !!row.is_on; ai.automationCfg[card] = row.config || {}; });
        res.hasAI = true;
      }
      res.ai = ai;
    } catch (e) { /* AI schema optional */ }
    try {
      var igData = pre ? (pre.integrations || []) : (await sb.from("integrations").select("*").eq("workspace_id", WS)).data;
      if (igData && igData.length) {
        igData.forEach(function (row) { res.integrations[row.provider] = Object.assign({ connected: !!row.connected, connectedAt: row.connected_at ? new Date(row.connected_at).getTime() : undefined }, row.config || {}); });
        res.hasInteg = true;
      }
    } catch (e) {}
    try {
      var sqData = pre ? (pre.sequences || []) : (await sb.from("sequences").select("*").eq("workspace_id", WS).order("created_at", { ascending: true })).data;
      if (sqData && sqData.length) {
        res.sequences = sqData.map(function (row) { return { id: row.id, name: row.name, trigger: row.trigger, on: row.is_on !== false, reward: row.reward || undefined, steps: row.steps || [] }; });
        res.hasSeq = true;
      }
    } catch (e) {}
    return res;
  }

  // write settings.{ai,integrations,sequences} → the typed tables — when they
  // changed. Every save used to re-send all four (five requests, in sequence,
  // on every edit anywhere in the app); now a save whose AI/integration/
  // sequence payload matches the last one that fully landed skips them.
  var aiKey = null;
  async function persistAI(state) {
    if (!sb || !state) return;
    var s = state.settings || {};
    var cat = aiCatalog();

    // ai_settings (single row). tracked_number + retell_agent_id are server-
    // managed (provisioning), so we deliberately omit them — upsert only
    // touches the columns we pass, leaving provisioned values intact.
    var ai = s.ai || {}, recept = ai.receptionist || {};
    var aiRow = {
      workspace_id: WS,
      receptionist_on: !!recept.enabled, voice_engine: recept.voiceEngine || "s2s_mini", agent_voice: recept.voice || "warm-f",
      answer_mode: recept.mode || "afterhours", greeting: recept.greeting || null,
      can_book: recept.canBook !== false, text_link: recept.textLink !== false,
      routing: ai.routing || {}, billing: ai.billing || {},
      data: Object.assign({}, ai.data, recept.number ? { forwardNumber: recept.number } : {}),
    };

    // ai_automations — a row per catalog card, keyed by canonical def id, with
    // the toggle (materializing the card default) + saved config. Message cards
    // get send defaults materialized so the worker can actually send.
    var autos = ai.automations || {}, cfgs = ai.automationCfg || {};
    var rows = cat.map(function (card) {
      var on = autos[card.id] != null ? !!autos[card.id] : !!card.default;
      var cfg = cfgs[card.id] || {};
      // Send defaults. Channel defaults to EMAIL, not SMS: outbound texting is
      // locked until the workspace's A2P 10DLC campaign is approved, so an
      // SMS default would leave every message automation switched On and
      // silently undeliverable. Email works from day one; the contractor
      // flips to SMS (or Both) once the registration clears.
      if (card.channelType === "message" || card.channelType === "jobevents")
        cfg = Object.assign({ channel: "email", quietHours: "8am – 8pm only", delay: "immediate", template: defaultTpl(card) }, cfg);
      if (card.newCfg && aiRowsAtLoad === false) cfg = Object.assign({}, card.newCfg, cfg);   // new workspace: on-by-default options
      return { workspace_id: WS, automation_id: card.def, channel_type: card.channelType, is_on: on, config: cfg };
    });

    // integrations — non-secret config only (secrets are stripped; the service
    // stores them in Vault on connect).
    var integ = s.integrations || {};
    var iRows = Object.keys(integ).map(function (prov) {
      var c = Object.assign({}, integ[prov]);
      var connected = !!c.connected, connectedAt = c.connectedAt;
      delete c.connected; delete c.connectedAt;
      (INTEG_SECRETS[prov] || []).forEach(function (k) { delete c[k]; });
      return { workspace_id: WS, provider: prov, connected: connected, config: c, connected_at: connectedAt ? new Date(connectedAt).toISOString() : null };
    });

    // sequences — upsert current + prune removed
    var seqRows = (s.sequences || []).map(function (q) {
      return { id: q.id, workspace_id: WS, name: q.name, trigger: q.trigger || "job_completed", is_on: q.on !== false, reward: q.reward || null, steps: q.steps || [] };
    });

    var key = JSON.stringify([aiRow, rows, iRows, seqRows]);
    if (key === aiKey) return;

    var aset = await sb.from("ai_settings").upsert(aiRow, { onConflict: "workspace_id" });
    if (aset.error) throw aset.error;
    if (rows.length) {
      var ares = await sb.from("ai_automations").upsert(rows, { onConflict: "workspace_id,automation_id" });
      if (ares.error) throw ares.error;
    }
    if (iRows.length) { var ires = await sb.from("integrations").upsert(iRows, { onConflict: "workspace_id,provider" }); if (ires.error) throw ires.error; }
    if (seqRows.length) {
      // (workspace_id, id) after migration #33: the default sequences carry
      // the same ids in every workspace ("seq-review"), so on the old key only
      // the first workspace to save ever owned them
      var sres = await upsertSynced("sequences", seqRows);
      if (sres.error) throw sres.error;
      var ids = seqRows.map(function (q) { return q.id; }).filter(Boolean);
      var del = sb.from("sequences").delete().eq("workspace_id", WS);
      if (ids.length) del = del.not("id", "in", inList(ids));
      var dres = await del; if (dres.error) throw dres.error;
    }
    aiKey = key;
  }

  // ---------- LOAD: assemble full app state from all tables ----------
  // assemble the app state from a workspace row + the typed-table bundle.
  // Shared by both load paths so the RPC and per-table results can't diverge.
  async function assembleState(row, pre) {
    // remember the revision we hydrated from — persist() refuses to write over
    // anything NEWER than this (the stale-tab clobber guard, below).
    loadedRev = Number(row && row.settings && row.settings._rev) || 0;
    var settings = Object.assign(
      { markup: row.markup != null ? Number(row.markup) : 2.0,
        laborRate: row.labor_rate != null ? Number(row.labor_rate) : 28,
        parcelProvider: row.parcel_provider || "wi",
        countyUrl: row.county_url || undefined },
      row.settings || {}
    );
    // the services this workspace runs (workspaces.verticals, #43): read off the
    // row, never written back — persist() strips it. Absent (migration not run)
    // → the registry's default, fence only.
    if (Array.isArray(row.verticals)) settings.verticals = row.verticals.slice();
    else delete settings.verticals;
    // typed tables are authoritative for AI / integrations / sequences
    try {
      var aiData = await loadAI(pre);   // pre = pre-fetched rows (RPC), else undefined → queries
      if (aiData.hasAI) settings.ai = aiData.ai;
      if (aiData.hasInteg) settings.integrations = aiData.integrations;
      if (aiData.hasSeq) settings.sequences = aiData.sequences;
    } catch (e) { /* AI tables optional */ }
    return { settings: settings, company: row.company || {}, companyReady: row.company_ready !== false };
  }

  // ---- seen-rows ledger -----------------------------------------------------
  // Which row ids has THIS tab actually seen (hydrated or written)? The save's
  // prune may only delete rows on this ledger: "not in my local state" is NOT
  // proof a row should die — the automation service inserts widget/booking
  // leads (quotes' customer mirrors) directly, WITHOUT bumping settings._rev,
  // so a tab that loaded before the lead is rev-current, saves normally, and
  // the old wholesale prune deleted the lead's rows straight out of the DB
  // (the recurring "deploy wiped our quote/customer" report). Scoping deletes
  // to seen-and-now-removed ids makes an unseen row simply survive until the
  // next hydrate/realtime pulls it into view.
  //
  // The ledger is fed ONLY from states the app actually APPLIES — the app
  // calls noteApplied(state) at its apply sites (hydrate, stale/guard reload,
  // realtime merge), and persist() records what it wrote. Noting inside the
  // load paths would poison the ledger: the realtime handler loads and then
  // DROPS the result inside its echo window, and ids "seen" by a dropped load
  // are exactly the rows the next save would wrongly prune.
  var seenRows = {};   // table → Set(row ids)

  /* FW-015 / L10 — the quote row version this tab last saw, per quote id.
     Every write carries it so the database can tell a current write from a
     stale one, and every write reads the new one back — without adopting it,
     the NEXT save would look stale too and every save after the first would be
     flagged. It lives here rather than in app state because it is a storage
     concern: the app neither sets it nor should have to thread it through. */
  var quoteRev = {};   // quote id → rev as of our last read/write
  function noteQuoteRevs(quotes) {
    (quotes || []).forEach(function (q) {
      if (!q || !q.id) return;
      var r = Number(q._rev);
      if (isFinite(r)) quoteRev[q.id] = r;
    });
  }

  /* What the database actually stored for the quotes we just wrote. Two jobs:
     adopt the new revs, and surface any write the database judged STALE —
     meaning money, a signature or an approved change order was kept from the
     server rather than taken from us. Emitting it is the point: the old
     behaviour was a silent last-write-wins over a settled payment. */
  function onQuotesWritten(rows) {
    var conflicts = [];
    (rows || []).forEach(function (r) {
      if (!r || !r.id) return;
      var rev = Number(r.rev);
      if (isFinite(rev)) quoteRev[r.id] = rev;
      var d = r.data || {};
      if (d._conflict) {
        conflicts.push({
          id: r.id,
          customer: d.customer || null,
          depositPaid: Number(d.depositPaid) || 0,
          signed: !!(d.signature && d.signature.at),
          note: d._conflict.note || null,
        });
      }
    });
    if (conflicts.length) {
      lastQuoteConflicts = conflicts;
      emit("conflict", conflicts.length + " estimate" + (conflicts.length > 1 ? "s" : "")
        + " changed on the server while this tab was open — the server's payment, signature and approval details were kept.");
    }
  }
  var lastQuoteConflicts = null;
  function noteSeen(table, ids) { seenRows[table] = new Set(ids.filter(Boolean)); }
  function noteSeenFromState(st) {
    noteQuoteRevs(st.quotes);   // FW-015 / L10: adopt the revs we just read
    noteSeen("quotes", (st.quotes || []).map(function (q) { return q && q.id; }));
    noteSeen("customers", (st.customers || []).map(function (c) { return c && c.id; }));
    noteSeen("work_orders", (st.workOrders || []).map(function (w) { return w && w.id; }));
    noteSeen("projects", (st.projects || []).map(function (p) { return p && p.id; }));
    // products rows may sit under the legacy blob id OR the ws-scoped row id —
    // count both as seen so the owner's save still prunes its legacy rows.
    var pids = [];
    (st.customProducts || []).forEach(function (p) { if (p && p.id) { pids.push(p.id); pids.push(wsRowId(p.id)); } });
    noteSeen("products", pids);
    noteSyncedFromState(st);
  }

  // ---- synced-rows ledger (send only what changed) --------------------------
  // table → Map(row id → fingerprint of the row as the database holds it). The
  // save upserts only rows whose fingerprint moved (lib/syncdiff.ts); it used
  // to re-send every customer, job and estimate on every edit.
  //
  // Fed from two places, both statements of fact about the database:
  //   • a write that landed (syncTable records each slice as it succeeds; a
  //     failed slice records nothing, so the next save re-sends it), and
  //   • an applied state's rows that came VERBATIM from the latest load —
  //     loadedRows holds those exact objects. A rescued local-only record, a
  //     row this tab edited, or anything from an older load isn't in it, so
  //     it still goes out on the next save. Products are never seeded: their
  //     rows may still sit under the legacy blob id, and the first save has to
  //     rewrite them under wsRowId before the prune retires the legacy rows.
  // Being wrong in the "not seeded" direction only costs a redundant write.
  var syncedFp = {};          // table → Map(id → fingerprint)
  var loadedRows = null;      // WeakSet of row objects from the latest load
  function ledger(table) { return syncedFp[table] || (syncedFp[table] = new Map()); }
  function markLoaded(lists) {
    if (typeof WeakSet === "undefined") return;
    var set = new WeakSet();
    lists.forEach(function (list) { (list || []).forEach(function (o) { if (o && typeof o === "object") set.add(o); }); });
    loadedRows = set;
  }
  function noteSyncedFromState(st) {
    if (!loadedRows || !st) return;
    [["quotes", st.quotes, quoteRow], ["customers", st.customers, customerRow],
      ["work_orders", st.workOrders, workOrderRow], ["projects", st.projects, projectRow]].forEach(function (t) {
      var book = ledger(t[0]);
      (t[1] || []).forEach(function (o) {
        if (o && o.id && loadedRows.has(o)) book.set(String(o.id), rowFingerprint(t[2](o)));
      });
    });
  }
  // FW-007: a job with a crew update still in the offline outbox (clock-out,
  // note, "on my way" — lib/outbox.ts) isn't upserted by the diff sync until
  // the service has taken those updates: the service applies them to the row
  // itself, in order and once, and a whole-row write from this tab first would
  // race them. A held row stays OFF the ledger, so the first save after the
  // outbox drains sends it; it is still counted as present, so it's never pruned.
  var rowHold = null;         // (table, id) → true to hold the row back
  function holdBack(table, diff) {
    if (!rowHold || !diff || !diff.changed.length) return diff;
    var keep = diff.changed.filter(function (r) {
      try { return !(r && r.id != null && rowHold(table, String(r.id))); } catch (e) { return true; }
    });
    return keep.length === diff.changed.length ? diff : { changed: keep, fps: diff.fps, bytes: diff.bytes };
  }
  // what a wholesale-pruned table's id set was at its last landed prune
  var prunedKey = {};         // table → idSetKey
  var activityKey = null;     // the activity list as last written

  // FAST PATH: one RPC returns the whole workspace as a JSON blob. Returns the
  // assembled state, null (empty workspace), or `undefined` to signal the
  // caller to fall back to per-table reads (RPC not migrated yet / any error).
  async function loadViaRpc() {
    try {
      var r = await sb.rpc("fw_workspace_state", { p_ws: WS });
      if (r.error || !r.data) return undefined;          // function missing / error → fall back
      var d = r.data;
      var row = d.workspace || null;
      var hasAny = row || (d.products && d.products.length) || (d.projects && d.projects.length) || (d.quotes && d.quotes.length);
      if (!hasAny) return null;
      var base = await assembleState(row || {}, {
        ai_settings: d.ai_settings || null, ai_automations: d.ai_automations || [],
        integrations: d.integrations || [], sequences: d.sequences || [],
      });
      // FW-014 / L09: the hydration function withholds collections from a
      // restricted role, so what comes back is a SLICE of the workspace, not
      // the workspace. The save path prunes any cloud row this state doesn't
      // contain — so saving from a crew session would delete every quote,
      // product and project in the workspace. `partial` makes the state
      // read-only for exactly that reason; persist() refuses to write it.
      partialHydration = !!d.partial;
      hydratedRole = d.role || null;
      // rows exactly as stored (unWsCrew hands back a new object only when it
      // had to rewrite one — that job isn't verbatim, so it isn't marked)
      markLoaded([d.quotes, d.customers, d.work_orders, d.projects]);
      return Object.assign(base, {
        customProducts: d.products || [],
        projects: d.projects || [],
        quotes: d.quotes || [],
        customers: d.customers || [],
        workOrders: (d.work_orders || []).map(unWsCrew),
        activity: (d.activity || []).map(function (a) { return { text: a.body, time: a.time_label || "Just now" }; }),
      });
    } catch (e) { return undefined; }                    // anything unexpected → fall back
  }

  // FALLBACK: the proven per-table reads (used when the RPC isn't available).
  async function loadViaTables() {
    var ws = await sb.from("workspaces").select("*").eq("id", WS).maybeSingle();
    if (ws.error) throw ws.error;
    var pr = await sb.from("products").select("data").eq("workspace_id", WS); if (pr.error) throw pr.error;
    var pj = await sb.from("projects").select("data").eq("workspace_id", WS); if (pj.error) throw pj.error;
    var qt = await sb.from("quotes").select("data").eq("workspace_id", WS); if (qt.error) throw qt.error;
    var cu = await sb.from("customers").select("data").eq("workspace_id", WS); if (cu.error) throw cu.error;
    var wo = await sb.from("work_orders").select("data").eq("workspace_id", WS); if (wo.error) throw wo.error;
    var ac = await sb.from("activity").select("body, time_label, position").eq("workspace_id", WS).order("position", { ascending: true });
    if (ac.error) throw ac.error;
    var hasAny = ws.data || (pr.data && pr.data.length) || (pj.data && pj.data.length) || (qt.data && qt.data.length);
    if (!hasAny) return null;
    var base = await assembleState(ws.data || {}, undefined);
    var blob = function (r) { return r.data; };
    markLoaded([(qt.data || []).map(blob), (cu.data || []).map(blob), (wo.data || []).map(blob), (pj.data || []).map(blob)]);
    return Object.assign(base, {
      customProducts: (pr.data || []).map(function (r) { return r.data; }),
      projects: (pj.data || []).map(function (r) { return r.data; }),
      quotes: (qt.data || []).map(function (r) { return r.data; }),
      customers: (cu.data || []).map(function (r) { return r.data; }),
      workOrders: (wo.data || []).map(function (r) { return unWsCrew(r.data); }),
      activity: (ac.data || []).map(function (r) { return { text: r.body, time: r.time_label || "Just now" }; }),
    });
  }

  async function load() {
    if (!sb) return null;
    if (demoActive()) { emit("demo"); return null; }   // sandbox: local state only
    emit("loading");
    try {
      // per-workspace RLS: cloud data requires a signed-in session (anon reads
      // are closed). Without one, stay on local state instead of erroring.
      var sess = await sb.auth.getSession();
      if (!(sess && sess.data && sess.data.session)) { emit("idle"); return null; }
      await resolveWorkspace();   // pick the logged-in user's workspace (multi-tenant)
      if (!WS) throw new Error("Couldn't resolve your workspace — check your connection and reload.");
      var state = await loadViaRpc();                    // one round-trip…
      if (state === undefined) state = await loadViaTables();   // …or the per-table fallback
      emit("idle");
      return state;
    } catch (e) { fail(e); }
  }

  // ---------- SAVE: explode state into normalized tables ----------
  // upsert current rows, then prune rows that no longer exist.
  //
  // Two prune modes:
  //   • pruneSeenOnly (user-data tables: products/projects/quotes/customers/
  //     work_orders) — delete ONLY ids on this tab's seen-rows ledger that are
  //     no longer in local state (a deliberate in-app delete). Rows this tab
  //     has never seen — server-inserted widget/booking leads, another
  //     device's records that realtime hasn't delivered — are left alone
  //     instead of being wiped by "not in my copy".
  //   • wholesale (mirror tables: crews/payments) — these are derived entirely
  //     from settings/quotes in THIS state, which the _rev stale-guard already
  //     protects, so "not in the derived set" really does mean "gone".
  // one upsert request carries at most this many rows / ~this much JSON
  var UPSERT_ROWS = 250, UPSERT_BYTES = 900 * 1024;

  // ---- plan-limit refusals ---------------------------------------------------
  // The database refuses a NEW product or estimate past the plan's counted cap
  // (supabase/migrate_2026-10-02_plan_limits.sql — the backstop behind
  // lib/limits.ts, for tabs and callers that never ran the app's check):
  //   { code: "P0001", message: "plan_limit: quotes", details: "Free allows 5 a month",
  //     hint: "Upgrade in Account & Billing" }            (PostgREST, HTTP 400)
  // One refused row fails its whole upsert statement, which would hold every
  // unrelated edit in the same slice hostage on every save. So a refused slice
  // is re-sent a row at a time: what lands goes on the synced-rows ledger as
  // usual; what the plan refuses stays OFF it, is reported once per save, and
  // goes out again on every later save — an upgrade makes it land.
  function planLimitOf(err) {
    return !!err && String(err.message || "").indexOf("plan_limit:") === 0;
  }
  var LIMIT_NOUN = { quotes: ["new estimate", "new estimates"], products: ["new product", "new products"] };
  function planLimitError(table, refused) {
    var n = refused.length, err = refused[0].error || {};
    var noun = LIMIT_NOUN[table] || ["new record", "new records"];
    var detail = String(err.details || err.detail || "").trim() || "your plan's limit is reached";
    var hint = String(err.hint || "").trim() || "Upgrade in Account & Billing";
    // "1 new estimate — Free allows 5 a month. Upgrade in Account & Billing."
    var e = new Error(n + " " + noun[n === 1 ? 0 : 1] + " — " + detail + ". " + hint + ".");
    e.code = "plan_limit";
    e.planLimit = table;
    e.refusedIds = refused.map(function (x) { return String(x.row.id); });
    return e;
  }
  var planRefused = {};       // table → Set(row ids the plan refused on the last save)

  // ---- sync keys: (workspace_id, id) once migration #33 has run -------------
  // (lib/synckeys.ts). The upsert names the key each table has: asked of the
  // database once per session (fw_sync_keys — missing before #33, meaning the
  // old `id` key), and flipped for that table if an upsert still names the
  // wrong one (42P10: the owner ran #33 while this tab was open).
  var keyModes = null, keyModesP = null;
  function syncKeyModes() {
    if (keyModes) return Promise.resolve(keyModes);
    if (!keyModesP) {
      keyModesP = Promise.resolve()
        .then(function () { return sb.rpc("fw_sync_keys"); })
        .then(function (r) { keyModes = keyModesFrom(r && !r.error ? r.data : null); return keyModes; },
              function () { keyModes = keyModesFrom(null); return keyModes; });
    }
    return keyModesP;
  }
  // upsert rows into a synced table on whichever key it has; `shape` adds the
  // .select() read-back when the caller wants the rows as the database left them
  async function upsertSynced(table, rows, shape) {
    var modes = await syncKeyModes();
    var run = function () {
      var q = sb.from(table).upsert(rows, { onConflict: conflictTarget(modes, table) });
      return shape ? shape(q) : q;
    };
    var r = await run();
    if (r && r.error && isConflictSpecError(r.error)) {
      modes[table] = flipMode(modes[table]);
      r = await run();
    }
    return r;
  }

  async function syncTable(table, rows, mapRow, opts) {
    var mapped = rows.map(mapRow);
    // prune ids come from the MAPPED rows — a mapper may rewrite the row id
    // (wsRowId below), and pruning against the pre-map ids would delete the
    // rows we just upserted. ALL current rows count, sent or not: an unchanged
    // row is still very much alive.
    var ids = mapped.map(function (r) { return r.id; }).filter(Boolean);
    // send only rows that differ from what the database holds (the synced-rows
    // ledger above), a slice at a time; each slice that lands goes on the
    // ledger, so a failure part-way re-sends only what didn't land
    var book = ledger(table);
    var diff = holdBack(table, changedRows(mapped, book));
    // rows the plan refused last time go on their own, so they don't fail the
    // slice everything else rides in again
    var held = planRefused[table] || new Set();
    var retry = diff.changed.filter(function (r) { return r && r.id != null && held.has(String(r.id)); });
    var fresh = retry.length ? diff.changed.filter(function (r) { return retry.indexOf(r) < 0; }) : diff.changed;
    var slices = upsertSlices(fresh, diff.bytes, UPSERT_ROWS, UPSERT_BYTES);

    // `readBack` asks for the rows AS THE DATABASE LEFT THEM. Quotes need it
    // (FW-015 / L10): a trigger rewrites the money/signature fields and bumps
    // the row version, so what landed is not what we sent, and this tab has
    // to know both.
    async function send(slice) {
      return await upsertSynced(table, slice, opts && opts.readBack ? function (q) { return q.select(opts.readBack); } : null);
    }
    function landed(slice, data) {
      if (opts && opts.onRows) { try { opts.onRows(data || []); } catch (e) {} }
      // with readBack, only rows the database handed back count as landed
      var back = opts && opts.readBack ? new Set((data || []).map(function (r) { return r && String(r.id); })) : null;
      slice.forEach(function (r) {
        var id = r && r.id != null ? String(r.id) : null;
        if (id && diff.fps[id] && (!back || back.has(id))) book.set(id, diff.fps[id]);
      });
    }
    // one row per request; returns the rows the plan refused. Any other
    // error fails the table exactly as a failed slice always has.
    async function sendEach(list) {
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var one = await send([list[i]]);
        if (one.error) {
          if (planLimitOf(one.error)) { out.push({ row: list[i], error: one.error }); continue; }
          throw one.error;
        }
        landed([list[i]], one.data);
      }
      return out;
    }

    var refused = [];
    for (var si = 0; si < slices.length; si++) {
      var up = await send(slices[si]);
      if (up.error && planLimitOf(up.error)) {
        refused = refused.concat(slices[si].length > 1 ? await sendEach(slices[si]) : [{ row: slices[si][0], error: up.error }]);
        continue;
      }
      if (up.error) throw up.error;
      landed(slices[si], up.data);
    }
    if (retry.length) refused = refused.concat(await sendEach(retry));

    if (opts && opts.pruneSeenOnly) {
      // never hydrated this table (fresh tab pre-load) → nothing provably
      // deletable → prune nothing.
      var prune = pruneIds(seenRows[table], ids);
      // in slices: the id list rides in the request URL, and a few hundred ids
      // in one filter runs past the gateway's URL limit (a big import undone,
      // or a large price book's legacy row ids on the first save after a
      // reload). A slice that fails throws before the ledger moves, so the
      // next save retries the rest; re-deleting a gone id is a no-op.
      for (var pi = 0; pi < prune.length; pi += 100) {
        var gone = prune.slice(pi, pi + 100);
        var sres = await sb.from(table).delete().eq("workspace_id", WS).in("id", gone);
        if (sres.error) throw sres.error;
        gone.forEach(function (id) { book.delete(String(id)); });
      }
      seenRows[table] = new Set(ids);   // what this tab just wrote is now its ledger
      // The upserts go before the prune, so an estimate deleted and another
      // created in the same edit meet the OLD count. Once the delete has
      // landed, what the plan refused gets one more try.
      if (refused.length && prune.length) refused = await sendEach(refused.map(function (x) { return x.row; }));
    } else {
      // prune anything not in the current set — only when the set itself moved
      // since the last prune that landed; an unchanged set has nothing to delete
      var key = WS + "|" + idSetKey(ids);
      if (prunedKey[table] !== key) {
        var del = sb.from(table).delete().eq("workspace_id", WS);
        if (ids.length) del = del.not("id", "in", inList(ids));
        var res = await del;
        if (res.error) throw res.error;
        prunedKey[table] = key;
        var keep = new Set(ids.map(String));
        Array.from(book.keys()).forEach(function (id) { if (!keep.has(id)) book.delete(id); });
      }
    }

    planRefused[table] = new Set(refused.map(function (x) { return String(x.row.id); }));
    if (refused.length) {
      // this tab's idea of the plan may be stale (a card failed, a sub
      // ended): re-sync it so the screens show the limit too
      try { if (window.VFP_RESYNC_PLAN) window.VFP_RESYNC_PLAN(); } catch (e) {}
      throw planLimitError(table, refused);
    }
  }

  // Workspace-scoped row id for MIRROR tables (crews, products) whose app-level
  // ids are NOT unique across workspaces: default crews ship as "crew-2man"/
  // "crew-3man" in every account, and a customized built-in product keeps its
  // catalog id ("cedar-6-privacy"). With a bare-`id` PK the first workspace to
  // save owns that row forever; every other workspace's upsert then hits an
  // ON CONFLICT UPDATE on a row RLS won't let it touch → 403 on every save.
  // Prefixing the ROW id with the workspace makes each workspace's rows its
  // own; the lossless `data` blob keeps the app-level id, and nothing reads
  // these tables back by row id (the app loads products from `data`, and crews
  // only from workspaces.settings).
  function wsRowId(id) {
    var p = WS + ":";
    return String(id).indexOf(p) === 0 ? String(id) : p + id;
  }
  // inverse for work orders read back into state: the `data` blob already
  // carries the app-level crewId, but strip the "<ws>:" prefix defensively so a
  // namespaced crew_id can never leak into w.crewId.
  function unWsCrew(w) {
    if (!w || !w.crewId) return w;
    var p = WS + ":";
    return String(w.crewId).indexOf(p) === 0 ? Object.assign({}, w, { crewId: String(w.crewId).slice(p.length) }) : w;
  }

  // ---- app record → table row ----------------------------------------------
  // One mapper per table, shared by the save and the synced-rows ledger (which
  // fingerprints a loaded record exactly as the save would write it).

  // products — wsRowId: a customized built-in keeps its catalog id
  // ("cedar-6-privacy"), which collides across workspaces on the bare-id PK.
  // The app loads products from `data`, so the row id is free to scope.
  function productRow(p) {
    return { id: wsRowId(p.id), workspace_id: WS, name: p.name, sub: p.sub, category: p.category,
      color: p.color, panel_width: num(p.panelWidth), panel_cost: num(p.panelCost),
      post_kit: num(p.postKit), height_ft: num(p.heightFt || p.height), all_steel: !!p.allSteel, data: p };
  }
  function projectRow(p) {
    return { id: p.id, workspace_id: WS, job: p.job, customer: p.customer, address: p.address,
      linear_feet: num(p.lf), total: num(p.total), product_name: p.productName, product_id: p.productId,
      status: p.status || "draft", from_quote: p.fromQuote || null, snapshot: p.snapshot || null, data: p,
      created_at: p.createdAt ? new Date(p.createdAt).toISOString() : new Date().toISOString() };
  }
  // quotes carry the row version this tab last saw (FW-015 / L10, see persist)
  function quoteRow(q) {
    var rev = quoteRev[q.id];
    if (!isFinite(rev)) rev = isFinite(Number(q._rev)) ? Number(q._rev) : 0;
    return { id: q.id, workspace_id: WS, customer: q.customer, phone: q.phone, email: q.email,
      address: q.address, linear_feet: num(q.lf), total: num(q.total), product_name: q.productName,
      product_id: q.productId, status: q.status || "pending", deposit_paid: num(q.depositPaid),
      inputs: q.inputs || null, fences: q.fences || null,
      data: Object.assign({}, q, { _rev: rev }),
      created_at: q.createdAt ? new Date(q.createdAt).toISOString() : new Date().toISOString() };
  }
  function customerRow(c) {
    return { id: c.id, workspace_id: WS, name: c.name, phone: c.phone || null, email: c.email || null,
      address: c.address || null, notes: c.notes || null, data: c };
  }
  function workOrderRow(w) {
    return { id: w.id, workspace_id: WS, quote_id: w.quoteId || null, project_id: w.projectId || null,
      // crews rows are stored under wsRowId(c.id) — the FK must match or every
      // job with a crew bounces (23503) and never reaches the cloud
      customer_id: w.customerId || null, crew_id: w.crewId ? wsRowId(w.crewId) : null, title: w.title,
      status: w.status || "scheduled", scheduled_start: w.scheduledStart || null, scheduled_end: w.scheduledEnd || null,
      total: num(w.total), crew_hours: num(w.crewHours), notes: w.notes || null, data: w };
  }

  async function persist(state) {
    if (!sb || !state) return;
    if (demoActive()) return "demo";
    // RLS: only signed-in users write to the cloud. (Previously anon/local-mode
    // saves silently landed in the shared default workspace — that was a bug.)
    // Report it — returning undefined here made flush() claim "saved" while
    // nothing was written.
    var sess = await sb.auth.getSession();
    if (!(sess && sess.data && sess.data.session)) return "nosession";

    // ---- role-restricted hydration (FW-014 / L09) --------------------------
    // This tab hydrated a SLICE of the workspace, because the caller's role
    // withholds collections: crew gets no quotes, no price book, no projects,
    // and only their own crew's jobs. The save below writes the whole settings
    // jsonb and PRUNES any cloud row this state doesn't contain — so a
    // whole-state save from a crew session would delete every quote, product
    // and project in the workspace.
    //
    // Refusing outright would break Crew Mode, whose time logging goes through
    // the same state → save path as everything else. So a restricted session
    // takes a NARROW path instead: it writes only the tables its role may write
    // (work orders — its own work), prunes nothing, and doesn't touch the
    // workspace row. See restrictedPersist below.
    if (partialHydration) return restrictedPersist(state);
    // A workspace that never resolved (lookup threw) must not be written to —
    // WS would be null or the shared default. Surface it as a sync error.
    await resolveWorkspace();
    if (!WS) throw new Error("Couldn't resolve your workspace — holding the save. Check your connection and try again.");

    // ---- stale-tab clobber guard -------------------------------------------
    // The save below writes the WHOLE settings jsonb and prunes rows that
    // aren't in this tab's state — from a tab that's been open (or asleep) for
    // hours, that overwrites everything anyone else saved since. So: check the
    // revision counter first. If the cloud moved past what THIS tab loaded,
    // refuse the write and report "stale" — the app pulls the fresh state and
    // tells the operator instead of silently eating the newer data. (The
    // probe→write pair isn't atomic, but the failure this exists for — a
    // long-stale tab — is hours wide, not milliseconds.)
    var probe = await sb.from("workspaces").select("settings").eq("id", WS).maybeSingle();
    // FAIL CLOSED: a probe error used to fall through to the write — the one
    // moment a stale tab is most likely to clobber (flaky network, RLS hiccup)
    // was the one moment the guard stood down. Hold the write instead; it's
    // retried on the next edit like any failed save.
    if (probe.error) throw new Error("Couldn't check the cloud revision before saving — holding the write. (" + (probe.error.message || probe.error.code || "probe failed") + ")");
    var remoteRev = Number(probe.data && probe.data.settings && probe.data.settings._rev) || 0;
    if (remoteRev > loadedRev) return "stale";

    // ---- empty-wipe circuit breaker ----------------------------------------
    // The sync below prunes any cloud row this state doesn't contain. A tab
    // holding an effectively-EMPTY state (fresh browser profile, cleared
    // storage, a boot whose cloud read failed) must therefore never save over
    // a workspace that has real data — that write IS the wipe. Costs two HEAD
    // counts, and only on the rare empty-state save.
    var localEmpty = !((state.quotes || []).length || (state.customers || []).length
      || (state.workOrders || []).length || (state.projects || []).length);
    if (localEmpty) {
      var qc = await sb.from("quotes").select("id", { count: "exact", head: true }).eq("workspace_id", WS);
      var cc = await sb.from("customers").select("id", { count: "exact", head: true }).eq("workspace_id", WS);
      // FAIL CLOSED: if we can't verify the cloud is actually empty, refuse the
      // save (throw → "error" status → the write is retried on the next edit).
      // The old fail-open here let an empty tab wipe a workspace whenever the
      // count probe itself errored — the exact moment (outage, RLS hiccup) an
      // empty local state is most likely.
      if (qc.error || cc.error) throw new Error("Couldn't verify cloud state before an empty-state save — holding the write. (" + ((qc.error || cc.error).message || "probe failed") + ")");
      if (((qc.count || 0) + (cc.count || 0)) > 0) return "guard";
    }

    var s = state.settings || {};
    // AI / integrations / sequences now live in typed tables — keep them OUT of
    // the workspaces.settings jsonb (avoids duplicating data and leaking secrets
    // into an anon-readable blob).
    var sClean = Object.assign({}, s);
    delete sClean.ai; delete sClean.integrations; delete sClean.sequences;
    // the services list is the server's (workspaces.verticals, guarded by #43's
    // trigger); it rides settings only as a read-only hydrate
    delete sClean.verticals;
    // FW-011 / L06: the A2P registration is server-owned (public.sms_registrations).
    // This save writes the WHOLE settings jsonb, so carrying a copy of the
    // registration here meant a stale tab could erase a real approval and a
    // crafted one could forge an approval the carriers never gave. Send only
    // the two fields the contractor actually edits; a database trigger restores
    // the rest either way, but there's no reason to send it and rely on that.
    if (sClean.sms) {
      sClean.sms = {
        forwardTo: sClean.sms.forwardTo || "",
        enabled: !!sClean.sms.enabled,
      };
    }
    // bump the revision this write carries; adopted as OUR rev once it lands
    var nextRev = loadedRev + 1;
    sClean._rev = nextRev;
    sClean._revAt = new Date().toISOString();

    // 1. workspace (parent row — must exist first for FKs)
    var wsRow = {
      id: WS,
      markup: num(s.markup) != null ? num(s.markup) : 2.0,
      labor_rate: num(s.laborRate) != null ? num(s.laborRate) : 28,
      parcel_provider: s.parcelProvider || "wi",
      county_url: s.countyUrl || null,
      company_ready: state.companyReady !== false,
      company: state.company || {},
      settings: sClean,
      updated_at: new Date().toISOString(),
    };
    var co = state.company || {};
    if (co.name) wsRow.company_name = co.name;
    if (co.phone) wsRow.company_phone = co.phone;
    if (co.email) wsRow.company_email = co.email;
    if (co.address) wsRow.company_address = co.address;
    var w = await sb.from("workspaces").upsert(wsRow, { onConflict: "id" });
    if (w.error) throw w.error;
    loadedRev = nextRev;   // our write is now the newest revision

    // ---- per-table isolation -----------------------------------------------
    // One denied table (RLS/grant drift, a bad column) must never hold the
    // tables AFTER it hostage — a crews 403 was silently keeping customers &
    // work orders out of the cloud on EVERY save. Sync everything syncable,
    // then report exactly what failed (thrown at the end → red pill + reason).
    var failedTables = [];
    async function trySync(label, fn) {
      try { await fn(); } catch (e) {
        // a plan-limit refusal already reads as a sentence ("1 new estimate —
        // Free allows 5 a month. Upgrade in Account & Billing."): everything
        // else in the table saved, so it isn't "products (…)"
        failedTables.push(e && e.planLimit ? e.message : label + " (" + ((e && (e.message || e.code)) || e) + ")");
      }
    }

    // 2. products (productRow: ws-scoped row ids)
    await trySync("products", function () {
      return syncTable("products", state.customProducts || [], productRow, { pruneSeenOnly: true });
    });

    // 3. projects
    await trySync("projects", function () {
      return syncTable("projects", state.projects || [], projectRow, { pruneSeenOnly: true });
    });

    // 4. quotes
    //
    // FW-015 / L10: this write carries the quote's `_rev` as this tab loaded
    // it. The database trigger (migrate_2026-09-17_quote_writes.sql) compares
    // it against the row's, restores the server-owned money/signature/approval
    // fields from the row either way, reconciles depositPaid against the
    // payment ledger, and marks data._conflict when the write was behind. So a
    // stale tab editing an unrelated field can no longer zero a deposit or
    // erase a signature, and the app can tell the operator it happened.
    //
    // status and deposit_paid are still sent, because a genuinely NEW quote
    // needs them and only the UPDATE trigger overrides them: it recomputes
    // deposit_paid from the ledger and refuses to walk a paid quote back to
    // unpaid. So the column mirror stays right without this tab having to be.
    //
    // Only CHANGED quotes are written now (the synced-rows ledger ignores the
    // _rev itself), so an estimate this tab didn't touch is no longer re-sent
    // with an old rev on every save — which also stops those untouched
    // estimates being flagged stale when the server moved them.
    await trySync("quotes", function () {
      return syncTable("quotes", state.quotes || [], quoteRow, { pruneSeenOnly: true, readBack: "id, rev, data", onRows: onQuotesWritten });
    });

    // 5. activity (small, ordered) — replace wholesale, when it changed
    await trySync("activity", async function () {
      var acts = (state.activity || []).slice(0, 20);
      var key = WS + "\n" + JSON.stringify(acts.map(function (a) { return [a.text, a.time || ""]; }));
      if (key === activityKey) return;
      var d = await sb.from("activity").delete().eq("workspace_id", WS);
      if (d.error) throw d.error;
      if (acts.length) {
        var ins = await sb.from("activity").insert(acts.map(function (a, i) {
          return { workspace_id: WS, body: a.text, time_label: a.time || "Just now", position: i };
        }));
        if (ins.error) throw ins.error;
      }
      activityKey = key;
    });

    // 6. crews — from settings (the app edits them there). wsRowId: the default
    // crews ship with the same ids ("crew-2man"/"crew-3man") in every
    // workspace → cross-workspace PK collision → RLS 403 on the
    // conflict-update. Nothing reads this table back, so the row id is free
    // to scope per workspace.
    await trySync("crews", function () {
      var crews = (s.crews || []).map(function (c) {
        return { id: wsRowId(c.id), workspace_id: WS, name: c.name, size: num(c.size), wage: num(c.wage),
          burden_pct: num(c.burdenPct), hours_per_day: num(c.hoursPerDay), foreman: c.foreman || null,
          phone: c.phone || null, color: c.color || null, is_active: c.id === s.activeCrewId,
          skills: c.skills || [], members: c.members || [], equipment: c.equipment || [], data: c };
      });
      return syncTable("crews", crews, function (r) { return r; });
    });

    // 7. customers — real CRM records from the store
    await trySync("customers", function () {
      return syncTable("customers", state.customers || [], customerRow, { pruneSeenOnly: true });
    });

    // 8. payments — one ledger row per quote with a deposit / paid
    await trySync("payments", function () {
      var pays = (state.quotes || []).filter(function (q) { return (Number(q.depositPaid) || 0) > 0 || q.status === "paid"; })
        .map(function (q) {
          var amt = Number(q.depositPaid) || Number(q.total) || 0;
          return { id: "pay-" + q.id, workspace_id: WS, quote_id: q.id, amount: amt,
            kind: q.depositPaid && q.depositPaid < q.total ? "deposit" : "full",
            method: "card", status: "paid", data: { quote: q.id, customer: q.customer } };
        });
      return syncTable("payments", pays, function (r) { return r; });
    });

    // 9. work orders — real records from the store
    await trySync("work orders", function () {
      return syncTable("work_orders", state.workOrders || [], workOrderRow, { pruneSeenOnly: true });
    });

    // 10. AI / integrations / sequences → typed tables. Isolated so a failure
    //     here never fails the core save (the AI schema may not be applied yet).
    try { await persistAI(state); } catch (e) { emit("error", "AI sync: " + (e && (e.message || e.code || String(e)))); }

    // everything syncable synced — now report what didn't (red pill + reason)
    if (failedTables.length) throw new Error("didn't save: " + failedTables.join(" · "));
  }

  /* FW-014 / L09 — the save path for a session that hydrated a restricted view.
     Writes ONLY the work orders this role may write, PRUNES NOTHING, and never
     touches the workspace row or the collections its hydration withheld. That
     keeps Crew Mode's time logging, photos and status changes working while
     making it structurally impossible for a crew session to erase the quotes,
     products and projects it was never shown.

     The database is the real boundary here (migrate_2026-09-17_roles_enforce.sql
     limits crew to their own crew's jobs and refuses everything else); this is
     the client not asking for a fight it would lose, and not mistaking a
     withheld collection for a deleted one. */
  async function restrictedPersist(state) {
    // only the jobs that changed, like the full save (synced-rows ledger)
    var book = ledger("work_orders");
    var diff = holdBack("work_orders", changedRows((state.workOrders || []).map(workOrderRow), book));
    var slices = upsertSlices(diff.changed, diff.bytes, UPSERT_ROWS, UPSERT_BYTES);
    for (var si = 0; si < slices.length; si++) {
      var r = await upsertSynced("work_orders", slices[si]);
      if (r.error) {
        // a policy refusal is the expected answer for a job that isn't theirs —
        // report it rather than presenting it as saved
        throw new Error("didn't save (" + (hydratedRole || "restricted") + " role): " + (r.error.message || r.error.code));
      }
      slices[si].forEach(function (w) { var id = w && w.id != null ? String(w.id) : null; if (id && diff.fps[id]) book.set(id, diff.fps[id]); });
    }
    return "saved";
  }

  // ---------- debounced flush ----------
  var timer = null, lastState = null, inflight = false, queued = false, lastSaveTs = 0;
  var loadedRev = 0;   // settings._rev this tab hydrated from (persist() guards on it)
  // FW-014 / L09: did this tab hydrate a ROLE-RESTRICTED slice of the
  // workspace? The save below prunes any cloud row this state doesn't contain,
  // so writing from a crew or sales session would delete the collections their
  // hydration withheld. Set by loadViaRpc from fw_workspace_state's own answer.
  var partialHydration = false;
  var hydratedRole = null;
  async function flush() {
    if (!sb || lastState == null) return;
    if (inflight) { queued = true; return; }
    inflight = true; emit("saving");
    try {
      var res = await persist(lastState);
      if (res === "stale" || res === "guard") {
        // the guard refused this tab's write (behind the cloud revision, or an
        // empty state over a workspace with real data — the wipe pattern).
        // Drop the pending write entirely: a retry would clobber the very data
        // the guard just protected. The app pulls fresh state + warns.
        lastState = null; queued = false;
        emit(res);
      } else if (res === "nosession") {
        // signed out — keep the pending state so a re-auth can still flush it,
        // and tell the pill the truth instead of "saved"
        emit("nosession");
      } else {
        lastSaveTs = Date.now();
        // stamp the last time a save fully landed — the boot-time rescue in
        // app.tsx uses it to spot records created AFTER the last good sync
        // (they exist nowhere but this device and must not be dropped when the
        // cloud state replaces local on the next boot).
        try { localStorage.setItem("fw_last_synced_at", String(lastSaveTs)); } catch (e2) {}
        emit("saved");
      }
    } catch (e) {
      emit("error", e && (e.message || e.code || String(e)));
    } finally {
      inflight = false;
      if (queued) { queued = false; flush(); }
    }
  }
  /* Push whatever this tab hasn't saved yet, and wait for it to land (or give
     up at maxMs). For a call that asks the SERVER to act on a quote as the
     cloud holds it — in-person signing — so the server signs the version on
     screen, and a change order recorded a second ago exists when it's
     approved. flush() queues behind a save already in flight rather than
     waiting for it, hence the poll. */
  async function saveSettled(maxMs) {
    var until = Date.now() + maxMs;
    if (timer) clearTimeout(timer);
    await flush();
    while ((inflight || queued) && Date.now() < until) await new Promise(function (r) { setTimeout(r, 100); });
  }

  /* In person: the customer signs (or approves a change order) on THIS device,
     through the service — the one place a signature can be recorded, because
     the quote trigger strips signature fields from every browser save. The
     answer carries the quote as the server now holds it; its row version is
     adopted here so this tab's next save isn't judged stale against the very
     signature it just asked for. */
  async function inPersonCall(path, body) {
    var base = window.VFP_API_BASE;
    if (!base) throw new Error("Signing in person needs the online service.");
    await resolveWorkspace();
    await saveSettled(10000);
    var r = await svcFetch(base.replace(/\/$/, "") + "/payments/in-person/" + encodeURIComponent(WS) + path, {
      method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify(body || {}),
    });
    var d = await r.json().catch(function () { return {}; });
    if (!r.ok) { var e = new Error(d.message || d.error || ("in-person " + r.status)); e.code = d.error || null; throw e; }
    if (d.quote) noteQuoteRevs([d.quote]);
    return d;
  }

  function save(state, immediate) {
    if (demoActive()) { lastState = null; emit("demo"); return; }   // sandbox: nothing is saved
    lastState = state;
    if (!sb) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, immediate ? 0 : 900);
  }

  // ---------- rolling snapshots + backup/restore ----------
  // Device-local safety net, independent of the cloud: at most one automatic
  // snapshot per 45 minutes, newest first, trimmed until it fits in
  // localStorage. Restore hands a full AppState back to the app (which then
  // saves it like any edit); download/import move the same JSON as a file.
  var SNAP_KEY = "fw_snapshots_v1";
  var SNAP_EVERY_MS = 45 * 60 * 1000;
  var SNAP_KEEP = 6;
  function snapList() { try { return JSON.parse(localStorage.getItem(SNAP_KEY) || "[]") || []; } catch (e) { return []; } }
  function snapWrite(list) {
    for (var keep = list.length; keep >= 1; keep--) {
      try { localStorage.setItem(SNAP_KEY, JSON.stringify(list.slice(0, keep))); return true; }
      catch (e) { /* quota — drop the oldest and retry */ }
    }
    return false;
  }
  function snapEntry(state, label) {
    return {
      at: Date.now(), label: label || null,
      quotes: (state.quotes || []).length, customers: (state.customers || []).length,
      workOrders: (state.workOrders || []).length, products: (state.customProducts || []).length,
      state: state,
    };
  }
  var snapLastAt = 0;   // cached so the every-keystroke caller skips without parsing
  function snapshotMaybe(state) {
    if (!state) return;
    try {
      if (!snapLastAt) { var l0 = snapList(); snapLastAt = (l0[0] && l0[0].at) || 1; }
      if (Date.now() - snapLastAt < SNAP_EVERY_MS) return;
      var list = snapList();
      if (snapWrite([snapEntry(state, null)].concat(list).slice(0, SNAP_KEEP))) snapLastAt = Date.now();
    } catch (e) { /* snapshots are best-effort, never break a save */ }
  }
  var backups = {
    auto: snapshotMaybe,   // throttled — safe to call on every state change
    list: function () { return snapList().map(function (s) { return { at: s.at, label: s.label, quotes: s.quotes, customers: s.customers, workOrders: s.workOrders, products: s.products }; }); },
    take: function (state, label) { var list = snapList(); var ok = snapWrite([snapEntry(state, label || "manual")].concat(list).slice(0, SNAP_KEEP)); if (ok) snapLastAt = Date.now(); return ok; },
    get: function (at) { var hit = snapList().find(function (s) { return s.at === at; }); return hit ? hit.state : null; },
  };

  // ---------- inventory: items / stock ledger / suppliers (real tables) ------
  // Phase 1 of the inventory build. Tables from supabase_schema_inventory.sql,
  // per-workspace RLS like everything else. Every quantity change goes through
  // move() so the ledger answers "who took 40 pickets Tuesday"; on_hand is the
  // materialized total. All calls resolve null/[] when the cloud isn't there —
  // the Inventory screen falls back to the legacy settings counts.
  async function stockSess() {
    if (!sb || demoActive()) return null;
    try { var r = await sb.auth.getSession(); return (r && r.data && r.data.session) || null; } catch (e) { return null; }
  }
  // "relation does not exist" (42P01 raw / PGRST205 via PostREST's schema
  // cache) → schema file not applied yet; the UI shows how. The message match
  // stays off "column" wording so a missing COLUMN never reads as no-schema.
  var tableMissing = function (e) { return e && (e.code === "42P01" || e.code === "PGRST205" || /does not exist|find the table/i.test(e.message || "")); };
  window.VFP_STOCK = {
    enabled: function () { return !!sb; },
    async status() {
      if (!(await stockSess())) return "off";
      var r = await sb.from("inventory_items").select("id", { count: "exact", head: true }).eq("workspace_id", WS);
      if (r.error) return tableMissing(r.error) ? "no-schema" : "error";
      return "ready";
    },
    /* active items by default; items(true) includes deleted ones too, so the
       ledger can still put a name on moves for items deleted since */
    async items(all) {
      if (!(await stockSess())) return null;
      var q = sb.from("inventory_items").select("*").eq("workspace_id", WS);
      if (!all) q = q.eq("active", true);
      var r = await q.order("name");
      if (r.error) { if (tableMissing(r.error)) return null; throw r.error; }
      // lift fields saveItem parked in data while the DB lacked their column
      return (r.data || []).map(function (x) {
        if (x && x.data) { for (var k in x.data) if (x[k] == null) x[k] = x.data[k]; }
        return x;
      });
    },
    async saveItem(item) {
      if (!(await stockSess())) return null;
      var row = Object.assign({}, item, { workspace_id: WS, updated_at: new Date().toISOString() });
      if (!row.id) row.id = "itm-" + Date.now() + "-" + Math.floor(Math.random() * 1000);
      // A DB from before a newer column (brand…) rejects the whole upsert with
      // PGRST204. Park the unknown field in the data jsonb and retry so the
      // save still lands; re-running supabase_schema_inventory.sql promotes it
      // to a real column (items() lifts parked fields back up on read).
      for (var tries = 0; tries < 4; tries++) {
        var r = await sb.from("inventory_items").upsert(row, { onConflict: "id" });
        if (!r.error) return row;
        var m = r.error.code === "PGRST204" && /'([^']+)' column/.exec(r.error.message || "");
        if (!m || m[1] === "data" || !(m[1] in row)) throw r.error;
        var fold = {}; fold[m[1]] = row[m[1]];
        row.data = Object.assign({}, row.data || {}, fold);
        delete row[m[1]];
      }
      throw new Error("item save kept bouncing off the schema");
    },
    async deleteItem(id) {
      if (!(await stockSess())) return;
      // soft delete — the ledger keeps referencing it
      var r = await sb.from("inventory_items").update({ active: false, updated_at: new Date().toISOString() }).eq("workspace_id", WS).eq("id", id);
      if (r.error) throw r.error;
    },
    /* the ONE write path for quantities: ledger row + materialized on_hand.
       qty is signed (+ in / − out). Returns the new on_hand. */
    async move(item, qty, kind, opts) {
      var sess = await stockSess();
      if (!sess || !item || !isFinite(qty) || !qty) return null;
      var by = (sess.user && (sess.user.user_metadata && sess.user.user_metadata.full_name || sess.user.email)) || "";
      var cost = item.cost != null ? Number(item.cost) : null;
      // atomic path: one statement writes the ledger row AND the on_hand
      // (supabase_schema_hardening.sql). Returns the updated item row.
      var rpc = await sb.rpc("apply_stock_move", {
        p_item_id: item.id, p_qty: qty, p_kind: kind || "adjust",
        p_ref: (opts && opts.ref) || null, p_note: (opts && opts.note) || null, p_by: by, p_cost: cost,
      });
      if (!rpc.error) {
        var row = Array.isArray(rpc.data) ? rpc.data[0] : rpc.data;
        if (row && row.on_hand != null) return Number(row.on_hand) || 0;
        // the move landed but no row came back: never re-apply it via the legacy path
        throw new Error("Stock move saved but the updated count didn't come back — refresh to see it");
      }
      var missing = rpc.error && (rpc.error.code === "42883" || rpc.error.code === "PGRST202" || /function .*apply_stock_move|could not find the function/i.test(rpc.error.message || ""));
      if (rpc.error && !missing) throw rpc.error;
      // legacy path (RPC not installed yet): ledger insert + read + update
      var mv = {
        id: "mv-" + Date.now() + "-" + Math.floor(Math.random() * 1000),
        workspace_id: WS, item_id: item.id, qty: qty, kind: kind || "adjust",
        ref: (opts && opts.ref) || null, note: (opts && opts.note) || null,
        by_name: by, cost: cost,
        at: new Date().toISOString(),
      };
      var i = await sb.from("stock_moves").insert(mv);
      if (i.error) throw i.error;
      var cur = await sb.from("inventory_items").select("on_hand").eq("workspace_id", WS).eq("id", item.id).maybeSingle();
      // a failed read must never be treated as "on hand 0" — that would
      // materialize a bogus count on top of a ledger row that already landed
      if (cur.error) throw cur.error;
      if (!cur.data) throw new Error("Item not found — couldn't update its on-hand count");
      var next = Math.max(0, (Number(cur.data.on_hand) || 0) + qty);
      var u = await sb.from("inventory_items").update({ on_hand: next, updated_at: new Date().toISOString() }).eq("workspace_id", WS).eq("id", item.id);
      if (u.error) throw u.error;
      return next;
    },
    /* count mode: set the absolute number; the delta lands on the ledger */
    async setCount(item, counted, note) {
      var delta = (Number(counted) || 0) - (Number(item.on_hand) || 0);
      if (!delta) return Number(item.on_hand) || 0;
      return this.move(item, delta, "count", { note: note || ("counted " + counted) });
    },
    async moves(limit, itemId) {
      if (!(await stockSess())) return [];
      var q = sb.from("stock_moves").select("*").eq("workspace_id", WS).order("at", { ascending: false }).limit(limit || 50);
      if (itemId) q = q.eq("item_id", itemId);
      var r = await q;
      if (r.error) throw r.error;
      return r.data || [];
    },
    /* PO receiving bridge: label-matched (engine_label first, then name) */
    async receiveByLabel(label, qty, ref) {
      if (!(await stockSess()) || !(qty > 0)) return null;
      var r = await sb.from("inventory_items").select("*").eq("workspace_id", WS).eq("active", true).eq("engine_label", label).limit(1);
      if (r.error) throw r.error;
      var item = r.data && r.data[0];
      if (!item) {
        r = await sb.from("inventory_items").select("*").eq("workspace_id", WS).eq("active", true).eq("name", label).limit(1);
        if (r.error) throw r.error;
        item = r.data && r.data[0];
      }
      if (!item) return null;
      return this.move(item, qty, "receive", { ref: ref });
    },
    /* PO receiving by SKU first (lib/stockmatch.ts): the supplier's number
       travels from the recipe component to the PO line, so an item the owner
       named differently in Inventory still takes the delivery; the label paths
       stay for lines without one. Returns { item, by, onHand }, or null when
       nothing tracked matches (the legacy counts still move). */
    async receiveLine(line, qty, ref) {
      if (!(await stockSess()) || !(qty > 0) || !line) return null;
      var items = await this.items();
      var m = stockMatch(items, { sku: line.sku, label: line.label });
      if (!m) return null;
      var onHand = await this.move(m.item, qty, "receive", { ref: ref, note: m.by === "sku" ? "by SKU " + String(line.sku).trim() : null });
      return { item: m.item, by: m.by, onHand: onHand };
    },
    async suppliers() {
      if (!(await stockSess())) return [];
      var r = await sb.from("suppliers").select("*").eq("workspace_id", WS).order("name");
      if (r.error) throw r.error;
      return r.data || [];
    },
    async saveSupplier(sup) {
      if (!(await stockSess())) return null;
      var row = Object.assign({}, sup, { workspace_id: WS });
      if (!row.id) row.id = "sup-" + Date.now();
      var r = await sb.from("suppliers").upsert(row, { onConflict: "id" });
      if (r.error) throw r.error;
      return row;
    },
    async deleteSupplier(id) {
      if (!(await stockSess())) return;
      var r = await sb.from("suppliers").delete().eq("workspace_id", WS).eq("id", id);
      if (r.error) throw r.error;
    },
    /* one-time seeding from the legacy settings.inventory counts + the open
       jobs' demand labels — every label becomes a tracked item. */
    async migrateLegacy(legacy, demandLabels) {
      if (!(await stockSess())) return 0;
      var have = await sb.from("inventory_items").select("id", { count: "exact", head: true }).eq("workspace_id", WS);
      if (have.error || (have.count || 0) > 0) return 0;
      var labels = {};
      Object.keys(legacy || {}).forEach(function (k) { labels[k] = legacy[k] || {}; });
      (demandLabels || []).forEach(function (k) { if (!labels[k]) labels[k] = {}; });
      var rows = Object.keys(labels).map(function (label, i) {
        var rec = labels[label];
        return {
          id: "itm-" + Date.now() + "-" + i, workspace_id: WS, name: label, engine_label: label,
          unit: /concrete|bag/i.test(label) ? "bag" : "ea",
          on_hand: Number(rec.onHand) || 0, reorder_at: rec.reorderAt != null ? Number(rec.reorderAt) : null,
          custom: false, active: true, updated_at: new Date().toISOString(),
        };
      });
      if (!rows.length) return 0;
      var r = await sb.from("inventory_items").insert(rows);
      if (r.error) { if (tableMissing(r.error)) return 0; throw r.error; }
      return rows.length;
    },
  };

  // ---------- Lead Radar: pool-permit lead feed + claims (real tables) --------
  // Tables from supabase_schema_leadradar.sql. pool_permits is MARKET-WIDE
  // shared data (every signed-in contractor reads the same feed — that's the
  // product); lead_claims is per-workspace under RLS. Claiming goes through
  // the radar_claim_lead RPC so quota + the 72h-exclusivity race are enforced
  // in the DATABASE — two contractors claiming in the same second can't both
  // win, and no client-side check is trusted. All calls resolve null/[] when
  // the cloud isn't there; the Lead Radar screen shows setup guidance.
  //
  // pool_permits is read with an EXPLICIT column list on purpose: the grant is
  // column-scoped (claimed_by and raw are withheld from clients), so a
  // select("*") would 403. Whether a claimed lead is YOURS comes from joining
  // your own claims, not from seeing who else holds it.
  // polygon is EXCLUDED from the list query on purpose (a parcel ring can run
  // to kilobytes; the feed must load fast on a phone) — lead() fetches it for
  // the one lead whose detail map is open.
  var RADAR_COLS = "id, county, permit_number, permit_date, permit_type, description, site_address, city, zip, apn, lat, lng, owner_name, parcel_area_sqft, perimeter_ft, est_fence_lf_low, est_fence_lf_high, fence_status, imagery_date, status, claim_expires_at, source_url, ingested_at, last_seen_at, source, state, signal_amount, source_id";
  // the ORIGINAL (v1) pool_permits shape: everything above minus the columns
  // the current schema adds additively (source, state, signal_amount,
  // source_id). A database still on v1 must not 400 every read — see below.
  var RADAR_COLS_V1 = "id, county, permit_number, permit_date, permit_type, description, site_address, city, zip, apn, lat, lng, owner_name, parcel_area_sqft, perimeter_ft, est_fence_lf_low, est_fence_lf_high, fence_status, imagery_date, status, claim_expires_at, source_url, ingested_at, last_seen_at";
  var colMissing = function (e) { return !!(e && (e.code === "42703" || /column .* does not exist/i.test(e.message || ""))); };
  var colDenied = function (e) { return !!(e && (e.code === "42501" || /permission denied/i.test(e.message || ""))); };
  // Which column era is this database on? Probed ONCE per page load with a
  // 1-row select of the newest column. undefined-column (or a grant that
  // predates it) → v1: every radar read drops to RADAR_COLS_V1 and rows get
  // source:'pool_permit' defaults, so a stale DB degrades to a working
  // permit-only radar with a LOUD banner — never a silent 400 the feed
  // swallows as "no data" (which is exactly how weeks of 0 leads hid).
  var RADAR_MODERN = null;   // null unknown · true modern · false v1
  async function radarCols() {
    if (RADAR_MODERN === null) {
      var p = await sb.from("pool_permits").select("source").limit(1);
      if (!p.error) RADAR_MODERN = true;
      else if (colMissing(p.error) || colDenied(p.error)) RADAR_MODERN = false;
      else return RADAR_COLS;   // transient/unknown error — don't cache a verdict
    }
    return RADAR_MODERN ? RADAR_COLS : RADAR_COLS_V1;
  }
  var radarV1Row = function (r) { return Object.assign({ source: "pool_permit", state: "FL" }, r); };
  window.VFP_RADAR = {
    // display-only mirror of the SQL quota ladder (radar_plan_quota is what's
    // ENFORCED; keep the two in sync). null = unlimited. Pricing is an open
    // call (PRD §8.1) — change it there first.
    QUOTA: { free: 0, starter: 5, pro: 20, growth: null, enterprise: null },
    PREMIUM_QUOTA: 50,     // paid-data (premium-source) claims per month per
                           // PAYING workspace — radar_premium_quota() is what's
                           // enforced (free/trial: 0, no open-pool exemption)
    OPEN_POOL_DAYS: 7,     // unclaimed this long → claiming is quota-free
    CLAIM_HOURS: 72,       // the exclusivity window
    enabled: function () { return !!sb; },
    async status() {
      if (!(await stockSess())) return "off";
      var r = await sb.from("pool_permits").select("id", { count: "exact", head: true });
      if (r.error) return tableMissing(r.error) ? "no-schema" : "error";
      return "ready";
    },
    /* true once the probe has established the DB is on the ORIGINAL schema —
       the page uses it to say exactly that instead of showing a quiet zero */
    schemaOutdated: function () { return RADAR_MODERN === false; },
    /* the feed: available + claimed leads, newest permit first (expired rows
       stay off it). One query, capped — the tab must render fast on a phone. */
    async leads(county, sources) {
      if (!(await stockSess())) return null;
      var cols = await radarCols();
      var q = sb.from("pool_permits").select(cols)
        .in("status", ["available", "claimed"])
        .order("permit_date", { ascending: false }).limit(200);
      if (county) q = q.eq("county", county);
      // the lens's lead types (lib/radar.lens.ts), filtered in the query — before the
      // 200-row cap, so a busy fence pipeline never starves the concrete feed (or vice versa)
      if (Array.isArray(sources) && sources.length && RADAR_MODERN !== false) q = q.in("source", sources);
      var r = await q;
      if (r.error) { if (tableMissing(r.error)) return null; throw r.error; }
      var rows = r.data || [];
      return RADAR_MODERN === false ? rows.map(radarV1Row) : rows;
    },
    /* one lead with its parcel ring, for the detail map */
    async lead(id) {
      if (!(await stockSess())) return null;
      var cols = await radarCols();
      var r = await sb.from("pool_permits").select(cols + ", polygon").eq("id", id).maybeSingle();
      if (r.error) { if (tableMissing(r.error)) return null; throw r.error; }
      return r.data ? (RADAR_MODERN === false ? radarV1Row(r.data) : r.data) : null;
    },
    /* this workspace's claims (RLS scopes the read), newest first. active=true
       → only live ones; otherwise the full history for the outcomes list. */
    async myClaims(activeOnly) {
      if (!(await stockSess())) return [];
      var q = sb.from("lead_claims").select("*").order("claimed_at", { ascending: false }).limit(200);
      if (activeOnly) q = q.is("released_at", null);
      var r = await q;
      if (r.error) { if (tableMissing(r.error)) return []; throw r.error; }
      return r.data || [];
    },
    /* claim / release / quota — thin RPC wrappers; the DB raises user-facing
       messages ("You've used all 5 lead claims…"), surface them verbatim. */
    async claim(permitId) {
      if (!(await stockSess())) throw new Error("Sign in to claim leads.");
      var r = await sb.rpc("radar_claim_lead", { p_permit_id: permitId });
      if (r.error) throw new Error(r.error.message || "Claim failed");
      return r.data;
    },
    async release(permitId) {
      if (!(await stockSess())) throw new Error("Sign in first.");
      var r = await sb.rpc("radar_release_lead", { p_permit_id: permitId });
      if (r.error) throw new Error(r.error.message || "Release failed");
      return r.data;
    },
    async quota() {
      if (!(await stockSess())) return null;
      var r = await sb.rpc("radar_quota_state");
      if (!r.error && r.data) return r.data;
      // RPC unreachable (stale PostgREST schema cache right after DDL, or the
      // function not applied yet) → a display-only fallback from the local
      // plan so the meter never shows a dead dash. Claims still enforce the
      // real quota server-side; `fallback` lets the UI say "≈".
      var planId = "free";
      try { planId = (JSON.parse(localStorage.getItem("vfp_state_v2")).settings || {}).plan || "free"; } catch (e) {}
      var q = window.VFP_RADAR.QUOTA[planId];
      return { plan: planId, quota: q === undefined ? 0 : q, used: null, remaining: q == null ? null : q, fallback: true };
    },
    /* Coverage — the source_status VIEW, where status is COMPUTED from run
       history (planned | never_run | error | stalled | stale | live). A
       source cannot read as live without a successful run that returned
       rows, so the panel can't assert something untrue. RLS keeps
       visibility='internal' sources out of everyone but listed viewers. */
    async sources() {
      if (!(await stockSess())) return [];
      var r = await sb.from("source_status").select("*").order("id");
      // null = the view doesn't exist yet (schema behind) — distinct from an
      // empty registry, so the tab can SAY "re-run the schema" instead of
      // silently hiding territory + coverage
      if (r.error) return tableMissing(r.error) ? null : [];
      return r.data || [];
    },
    /* Registry fallback for when source_status is missing (schema behind):
       read lead_sources directly so the tab can still SHOW what the sources
       are. Rows carry status "unknown" — run history isn't readable, and this
       panel never asserts a status it can't prove. */
    async sourcesRegistry() {
      if (!(await stockSess())) return null;
      var r = await sb.from("lead_sources")
        .select("id, label, region, county, state, lead_type, adapter, cadence, visibility, enabled, compliance_note")
        .order("id");
      if (r.error) return null;
      return (r.data || []).map(function (s) {
        return Object.assign({}, s, {
          automated: s.adapter === "accela" || s.adapter === "zillapi" || s.adapter === "parcel_age" || s.adapter === "municipal",
          premium: s.adapter === "zillapi",
          status: "unknown", last_run_at: null, last_run_rows: null,
        });
      });
    },
    /* ---- live record search: the whole ingested archive, not the curated
       feed. The feed hides expired rows (propensity window closed); search
       DOESN'T — "who moved 6–9 months ago" must reach older records, so the
       status filter spans available | claimed | expired. Date window filters
       cut on permit_date (the signal date: filed / closed / built).
       opts: { source, county, from, to, q, limit } — source is "all", one
       signal id, or an ARRAY of signal ids (the pool/hot-tub chip is one
       chip over two types); from/to are YYYY-MM-DD (from inclusive, to
       exclusive). Returns { rows, total } with an exact server-side count so
       the UI can say "412 matches, showing 300"; on a v1 database the result
       carries schemaOutdated:true and non-permit signals honestly return
       zero (those types cannot exist there). */
    async search(opts) {
      if (!(await stockSess())) return null;
      opts = opts || {};
      var cols = await radarCols();
      var v1 = RADAR_MODERN === false;
      var src = opts.source && opts.source !== "all"
        ? (Array.isArray(opts.source) ? opts.source : [opts.source]) : null;
      // v1 table: every row IS a permit — a permit-type filter is a no-op,
      // any other signal type can't match anything
      if (v1 && src && !src.some(function (s) { return s === "pool_permit" || s === "hot_tub_permit"; })) {
        return { rows: [], total: 0, schemaOutdated: true };
      }
      var q = sb.from("pool_permits").select(cols, { count: "exact" })
        .order("permit_date", { ascending: false })
        .limit(Math.min(500, opts.limit || 300));
      if (src && !v1) q = src.length === 1 ? q.eq("source", src[0]) : q.in("source", src);
      if (opts.county && opts.county !== "all") q = q.eq("county", opts.county);
      if (opts.from) q = q.gte("permit_date", opts.from);
      if (opts.to) q = q.lt("permit_date", opts.to);
      var text = String(opts.q || "").trim()
        // PostgREST .or() parses commas/parens as syntax and % as wildcard —
        // strip them so user text can't break (or widen) the filter
        .replace(/[,%()]/g, " ").replace(/\s+/g, " ").trim();
      if (text) {
        var pat = "*" + text + "*";
        q = q.or(["site_address", "city", "zip", "owner_name"].map(function (c) { return c + ".ilike." + pat; }).join(","));
      }
      var r = await q;
      if (r.error) { if (tableMissing(r.error)) return null; throw r.error; }
      var rows = r.data || [];
      if (v1) rows = rows.map(radarV1Row);
      return { rows: rows, total: r.count == null ? rows.length : r.count, schemaOutdated: v1 || undefined };
    },
    /* ---- territory: where this contractor works (feed scopes to it) ---- */
    async territories() {
      if (!(await stockSess())) return null;
      var r = await sb.from("account_territories").select("source_id, notify");
      if (r.error) { if (tableMissing(r.error)) return null; return []; }
      return r.data || [];
    },
    /* replace this workspace's territory picks wholesale.
       entries: [{source_id, notify}] — notify=true rows are "tell me when
       this market goes live" (demand data for which metro to build next). */
    async setTerritories(entries) {
      if (!(await stockSess())) throw new Error("Sign in first.");
      await resolveWorkspace();
      var rows = (entries || []).map(function (e) { return { workspace_id: WS, source_id: e.source_id, notify: !!e.notify }; });
      var cur = await sb.from("account_territories").select("source_id");
      if (cur.error) throw cur.error;
      var keep = new Set(rows.map(function (r) { return r.source_id; }));
      var drop = (cur.data || []).map(function (r) { return r.source_id; }).filter(function (id) { return !keep.has(id); });
      if (drop.length) {
        var d = await sb.from("account_territories").delete().eq("workspace_id", WS).in("source_id", drop);
        if (d.error) throw d.error;
      }
      if (rows.length) {
        var u = await sb.from("account_territories").upsert(rows, { onConflict: "workspace_id,source_id" });
        if (u.error) throw u.error;
      }
      return { ok: true };
    },
    /* outcome tracking — "confirmed closed jobs" is the only number that
       matters (PRD §7); these write the columns the update grant allows. */
    async setOutcome(claimId, outcome) {
      if (!(await stockSess())) return null;
      var r = await sb.from("lead_claims").update({ outcome: outcome }).eq("id", claimId);
      if (r.error) throw r.error;
      return { ok: true };
    },
    async linkQuote(claimId, quoteId) {
      if (!(await stockSess())) return null;
      var r = await sb.from("lead_claims").update({ quote_id: quoteId, outcome: "quoted" }).eq("id", claimId);
      if (r.error) throw r.error;
      return { ok: true };
    },
  };

  // ---------- realtime: reload full state when another device writes ----------
  function subscribe(onRemote) {
    if (!sb || demoActive()) return function () {};
    var rt = null;
    var ch = sb.channel("fw-sync")
      .on("postgres_changes", { event: "*", schema: "public" }, function () {
        // ignore the echo from our own save (recent, in-flight, or a pending debounced write)
        if (Date.now() - lastSaveTs < 6000 || inflight || timer || queued) return;
        if (rt) clearTimeout(rt);
        rt = setTimeout(function () {
          if (Date.now() - lastSaveTs < 6000 || inflight || timer || queued) return;
          load().then(function (st) { if (st) onRemote(st); }).catch(function () {});
        }, 800);
      })
      .subscribe();
    return function () { sb.removeChannel(ch); };
  }

  window.VFP_DB = {
    ready: ready, configured: ready, url: SUPA_URL, workspace: WS,
    load: load, save: save,
    // the app reports every state it APPLIES so the save's prune knows which
    // rows this tab has actually seen (see the seen-rows ledger above)
    noteApplied: noteSeenFromState,
    // FW-015 / L10: quotes whose last save the database judged stale, so the
    // app can tell the operator what the server kept instead of their copy.
    // Read-and-clear: reporting it twice would be noise.
    takeQuoteConflicts: function () { var c = lastQuoteConflicts; lastQuoteConflicts = null; return c; },
    flushNow: function () { if (timer) clearTimeout(timer); return flush(); },
    // true when this workspace had no automation rows when it loaded (a new
    // account): on-by-default options apply until they're saved (ai.tsx)
    aiFresh: function () { return aiRowsAtLoad === false; },
    subscribe: subscribe,
    backups: backups,
    // count of a workspace event type since an ISO timestamp (events table is
    // RLS-readable) — the widget settings use it for "X previews this month"
    eventsCount: async function (type, sinceIso) {
      if (!sb || demoActive()) return null;
      try {
        var sess = await sb.auth.getSession();
        if (!(sess && sess.data && sess.data.session)) return null;
        var q = sb.from("events").select("*", { count: "exact", head: true }).eq("workspace_id", WS).eq("type", type);
        if (sinceIso) q = q.gte("created_at", sinceIso);
        var r = await q;
        return r.error ? null : (r.count || 0);
      } catch (e) { return null; }
    },

    // ---------- automation service (optional) ----------
    // When window.VFP_API_BASE points at the deployed automation service, the
    // connect flow sends secrets there (→ Vault) instead of keeping them local.
    apiBase: function () { return window.VFP_API_BASE || null; },
    ws: function () { return WS; },
    /* handwritten-note photo → structured estimate fields (OpenAI vision on
       the service). Returns { fields } for the Quick-add form to prefill. */
    async scanQuote(image) {
      var base = window.VFP_API_BASE;
      if (!base) throw new Error("Scanning needs the online service (app.fenceworks.ai).");
      var r = await svcFetch(base.replace(/\/$/, "") + "/quote/scan", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ image: image }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.error || ("scan failed (" + r.status + ")"));
      return d;
    },
    async connectIntegration(provider, fields) {
      var base = window.VFP_API_BASE;
      if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/" + provider, {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(Object.assign({ workspaceId: WS }, fields || {})),
      });
      if (!r.ok) throw new Error("connect " + provider + " → " + r.status + " " + (await r.text()));
      return r.json();
    },
    async disconnectIntegration(provider) {
      var base = window.VFP_API_BASE;
      if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/" + provider + "/disconnect", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS }),
      });
      // a 403 is "owner/admin only" — say that, not a bare status code
      if (!r.ok) { var de = await r.json().catch(function () { return {}; }); throw new Error(de.message || ("disconnect " + provider + " → " + r.status)); }
      return r.json();
    },
    // OAuth start is a browser NAVIGATION (it can't carry the bearer token), so
    // ask the authed start-url endpoint for a one-time URL, then navigate there.
    // opts.landing (Stripe only): "login" opens Stripe's sign-in for someone who
    // already has Stripe, "register" its sign-up for someone who doesn't
    async oauthStart(provider, opts) {
      var base = window.VFP_API_BASE; if (!base) return null;
      var landing = opts && (opts.landing === "login" || opts.landing === "register") ? opts.landing : undefined;
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/" + provider + "/start-url", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, landing: landing }),
      });
      if (!r.ok) { var se = await r.json().catch(function () { return {}; }); throw new Error(se.message || ("oauth start " + provider + " → " + r.status)); }
      var d = await r.json();
      // Until Google verifies our OAuth app, its consent flow opens on a red
      // "Google hasn't verified this app" page. Say so BEFORE sending anyone
      // there, with the two clicks past it and exactly what we'll use — met
      // cold, that page reads like a hack. false = they chose not to go.
      if (d && d.url && d.googleUnverified && window.VFP_CONFIRM) {
        var go = await window.VFP_CONFIRM(googleHeadsUp(provider));
        if (!go) return false;
      }
      return (d && d.url) || null;
    },
    /* What Stripe is still waiting for on the contractor's connected account.
       The integration row only ever knew "connected or not", which is why a
       half-finished onboarding looked identical to one never started. */
    async stripeAccountStatus() {
      var base = window.VFP_API_BASE; if (!base) return null;
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/stripe/account", { headers: await authHeaders() });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.error || ("stripe account " + r.status));
      return d;
    },
    // where "Open Stripe dashboard" goes: their own Stripe dashboard (Standard),
    // or a single-use login link for a legacy Express account
    async stripeDashboardUrl() {
      var base = window.VFP_API_BASE; if (!base) return null;
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/stripe/dashboard-url", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.error || ("stripe dashboard " + r.status));
      return (d && d.url) || null;
    },
    // push a scheduled work order to the contractor's Google Calendar → { eventId }
    /* FW-027 — the two-way switch, where the scheduler can see it. It used to
       live only in the app's own settings blob, so the server had no idea
       whether the contractor wanted Google to move their jobs. */
    async gcalState(patch) {
      var base = window.VFP_API_BASE; if (!base) return null;
      var url = base.replace(/\/$/, "") + "/connect/gcal/state";
      var r = await svcFetch(url, patch
        ? { method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify(patch) }
        : { headers: await authHeaders() });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("gcal state " + r.status));
      return d;
    },
    async gcalSync(workOrder, calendarId) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/gcal/sync", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, workOrder: workOrder, calendarId: calendarId || undefined }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) { var e: any = new Error(d.error || ("gcal sync " + r.status)); throw e; }
      return d;
    },
    async gcalUnsync(eventId) {
      var base = window.VFP_API_BASE; if (!base || !eventId) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/gcal/unsync", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, eventId: eventId }),
      });
      if (!r.ok) throw new Error("gcal unsync " + r.status);
      return r.json();
    },
    // Google Business Profile: which business do reviews come from?
    async gbpLocations() {
      var base = window.VFP_API_BASE; if (!base) return { locations: [], current: null };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/gbp/locations", { headers: await authHeaders() });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("gbp " + r.status));
      return d;
    },
    async gbpSetLocation(reviewParent) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/gbp/location", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ reviewParent: reviewParent }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("gbp " + r.status));
      return d;
    },
    // Facebook: which Page do the social automations post to?
    async facebookPages() {
      var base = window.VFP_API_BASE; if (!base) return { pages: [], current: null };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/facebook/pages", { headers: await authHeaders() });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("facebook " + r.status));
      return d;
    },
    async facebookSetPage(pageId) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/facebook/page", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ pageId: pageId }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("facebook " + r.status));
      return d;
    },
    // social post approval queue — drafts the social_post / review_share
    /* FW-023 — messages held by "Require my approval before each send".
       Same three calls as the social queue: list, send, dismiss. Approving can
       come back { deferred } when quiet hours are in force — a human tapping
       Send doesn't make a 9pm text legal, so the server queues it. */
    async messagesPending() {
      var base = window.VFP_API_BASE; if (!base) return { drafts: [] };
      var r = await svcFetch(base.replace(/\/$/, "") + "/messages/pending", { headers: await authHeaders() });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("messages " + r.status));
      return d;
    },
    async messageApprove(eventId, body) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/messages/approve", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ eventId: eventId, body: body }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("approve " + r.status));
      return d;
    },
    /* the homeowner's concrete photos → measures the estimator verifies
       (server/src/concretescan.js; §13 item 10). { photos: [dataUrl | the
       request's own photo link], hints } → { scan, photos } */
    async concreteScan(body) {
      var base = window.VFP_API_BASE;
      if (!base) throw new Error("Reading photos needs the online service (app.fenceworks.ai).");
      var r = await svcFetch(base.replace(/\/$/, "") + "/concrete/scan", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(body || {}),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("scan failed (" + r.status + ")"));
      return d;
    },
    /* a purchase order to the supplier, sent from the service under the job's
       brand (server/src/purchasing.js; §13 item 18) — a real send, not a mailto: */
    async poEmail(body) {
      var base = window.VFP_API_BASE;
      if (!base) return { ok: false, skipped: "offline" };
      var r = await svcFetch(base.replace(/\/$/, "") + "/purchasing/po-email", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(body || {}),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("po-email " + r.status));
      return d;
    },
    /* FW-024 — a button the contractor pressed deliberately. Not an automation
       trigger: dispatchEvent skips any automation whose toggle is off, which is
       how "Follow up" and "Send reminder" managed to do nothing at all. */
    async messageSendNow(opts) {
      var base = window.VFP_API_BASE;
      if (!base) return { ok: false, skipped: "offline" };
      var r = await svcFetch(base.replace(/\/$/, "") + "/messages/send-now", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(opts || {}),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("send " + r.status));
      return d;
    },
    async messageDismiss(eventId) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/messages/dismiss", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ eventId: eventId }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("dismiss " + r.status));
      return d;
    },

    // ---- Reviews page (server/src/reviews.js) + GBP optimizer (gbpoptimizer.js) ----
    // One JSON call shape for all of them. Throws with the server's own message
    // (written for the contractor) and the code, so screens can branch on
    // "migration" / "not_connected" / "credits".
    async svcJson(path, method, body) {
      var base = window.VFP_API_BASE;
      if (!base) { var e0 = new Error("Connect to the Fence Works service to use this."); e0.code = "offline"; throw e0; }
      var init = { method: method || "GET", headers: await authHeaders() };
      if (body !== undefined) { init.headers = Object.assign({ "Content-Type": "application/json" }, init.headers); init.body = JSON.stringify(body); }
      var r = await svcFetch(base.replace(/\/$/, "") + path, init);
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) { var e = new Error(d.message || d.error || (path + " " + r.status)); e.code = d.error || r.status; e.status = r.status; throw e; }
      return d;
    },
    reviewsList() { return this.svcJson("/reviews"); },
    reviewAdd(review) { return this.svcJson("/reviews", "POST", review); },
    reviewUpdate(id, patch) { return this.svcJson("/reviews/" + encodeURIComponent(id) + "/update", "POST", patch); },
    reviewDelete(id) { return this.svcJson("/reviews/" + encodeURIComponent(id) + "/delete", "POST", {}); },
    reviewDraft(id) { return this.svcJson("/reviews/" + encodeURIComponent(id) + "/draft", "POST", {}); },
    reviewReply(id, text) { return this.svcJson("/reviews/" + encodeURIComponent(id) + "/reply", "POST", { text: text }); },
    reviewsSync() { return this.svcJson("/reviews/sync", "POST", {}); },
    reviewAudience(channel) { return this.svcJson("/reviews/campaign/audience?channel=" + encodeURIComponent(channel || "email")); },
    reviewImportPast(rows) { return this.svcJson("/reviews/campaign/import", "POST", { rows: rows }); },
    reviewCampaignSteps(channel) { return this.svcJson("/reviews/campaign/preview", "POST", { channel: channel }); },
    reviewCampaigns() { return this.svcJson("/reviews/campaigns"); },
    reviewCampaignLaunch(c) { return this.svcJson("/reviews/campaigns", "POST", c); },
    reviewCampaignStop(id) { return this.svcJson("/reviews/campaigns/" + encodeURIComponent(id) + "/stop", "POST", {}); },
    gbpAudit(fresh) { return this.svcJson("/gbp/audit" + (fresh ? "?fresh=1" : "")); },
    gbpSuggest(kind, differentiators) { return this.svcJson("/gbp/suggest", "POST", { kind: kind, differentiators: differentiators || "" }); },
    gbpPost(message) { return this.svcJson("/gbp/post", "POST", { message: message }); },
    // one-tap "Apply to Google" (explicit press; the old value is kept for Undo)
    gbpApply(field, value) { return this.svcJson("/gbp/apply", "POST", { field: field, value: value }); },
    gbpUndo(eventId) { return this.svcJson("/gbp/undo", "POST", { eventId: eventId }); },
    // ---- Website builder (server/src/sites/**, docs/SITES_CONTRACT.md §4) ----
    siteGet() { return this.svcJson("/sites/me"); },
    siteCreate(answers) { return this.svcJson("/sites/me", "POST", { slug: (answers && answers.slug) || undefined, answers: answers || {} }); },
    siteSaveDraft(draft) { return this.svcJson("/sites/me/draft", "POST", { draft: draft }); },
    sitePublish() { return this.svcJson("/sites/me/publish", "POST", {}); },
    siteUnpublish() { return this.svcJson("/sites/me/unpublish", "POST", {}); },
    siteRevisions() { return this.svcJson("/sites/me/revisions"); },
    siteRevert(rev) { return this.svcJson("/sites/me/revert", "POST", { rev: rev }); },
    // ---- Business phone line (server/src/voice/**, docs/VOIP_PLAN.md) ----
    lines() { return this.svcJson("/lines"); },
    lineBuy(opts) { return this.svcJson("/lines/buy", "POST", opts || {}); },
    lineAdopt(opts) { return this.svcJson("/lines/adopt", "POST", opts || {}); },
    lineRouting(id, routing) { return this.svcJson("/lines/" + encodeURIComponent(id) + "/routing", "PUT", { routing: routing }); },
    lineRelease(id) { return this.svcJson("/lines/" + encodeURIComponent(id) + "/release", "POST", {}); },
    lineTestCall(id, to) { return this.svcJson("/lines/" + encodeURIComponent(id) + "/test-call", "POST", { to: to }); },
    voicemailAudio(id) { return this.svcJson("/lines/voicemails/" + encodeURIComponent(id) + "/audio"); },
    voicemailListened(id) { return this.svcJson("/lines/voicemails/" + encodeURIComponent(id) + "/listened", "POST", {}); },
    siteRegenerate(section, answers) { return this.svcJson("/sites/me/regenerate", "POST", { section: section, answers: answers || undefined }); },
    siteSetSlug(slug) { return this.svcJson("/sites/me/slug", "POST", { slug: slug }); },
    siteDomainAdd(hostname) { return this.svcJson("/sites/me/domains", "POST", { hostname: hostname }); },
    siteDomainCheck(id) { return this.svcJson("/sites/me/domains/" + encodeURIComponent(id) + "/check", "POST", {}); },
    siteDomainPrimary(id) { return this.svcJson("/sites/me/domains/" + encodeURIComponent(id) + "/primary", "POST", {}); },
    siteDomainRemove(id) { return this.svcJson("/sites/me/domains/" + encodeURIComponent(id) + "/remove", "POST", {}); },
    siteLeads(days) { return this.svcJson("/sites/me/leads?days=" + encodeURIComponent(String(days || 30))); },
    siteUpload(dataUrl, kind) { return this.svcJson("/sites/me/upload", "POST", { dataUrl: dataUrl, kind: kind || "photo" }); },
    // blog posts drafted from finished jobs (server/src/sites/jobposts.js)
    sitePostSuggestions() { return this.svcJson("/sites/me/post-suggestions"); },
    sitePostDraft(workOrderId) { return this.svcJson("/sites/me/post-suggestions/draft", "POST", { workOrderId: workOrderId || undefined }); },
    sitePostAccept(id, post) { return this.svcJson("/sites/me/post-suggestions/" + encodeURIComponent(id) + "/accept", "POST", { post: post || undefined }); },
    sitePostDismiss(id) { return this.svcJson("/sites/me/post-suggestions/" + encodeURIComponent(id) + "/dismiss", "POST", {}); },

    // ---- job photos + documents as files (server/src/media.js, web/src/lib/media.ts) ----
    // The body is the file itself (not JSON): a photo → the public site-assets
    // bucket, a document → the private job-docs bucket. Throws with the
    // server's message and its status, which lib/media.ts decides retries on.
    async mediaSend(path, blob, query) {
      var base = window.VFP_API_BASE;
      if (!base) { var e0 = new Error("Connect to the Fence Works service to upload."); e0.code = "offline"; throw e0; }
      if (demoActive()) { var e1 = new Error("The sandbox doesn't upload files."); e1.code = "demo"; throw e1; }
      var qs = Object.keys(query || {}).filter(function (k) { return query[k] != null && query[k] !== ""; })
        .map(function (k) { return encodeURIComponent(k) + "=" + encodeURIComponent(String(query[k])); }).join("&");
      var r = await svcFetch(base.replace(/\/$/, "") + path + (qs ? "?" + qs : ""), {
        method: "POST", headers: Object.assign({ "Content-Type": (blob && blob.type) || "application/octet-stream" }, await authHeaders()), body: blob,
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) { var e = new Error(d.message || d.error || ("upload " + r.status)); e.code = d.error || r.status; e.status = r.status; throw e; }
      return d;
    },
    mediaUpload(blob, opts) { return this.mediaSend("/media/upload", blob, { key: opts && opts.key, year: opts && opts.year }); },
    mediaDocUpload(blob) { return this.mediaSend("/media/doc", blob, {}); },
    mediaDocUrl(path, opts) { return this.svcJson("/media/doc-url", "POST", { path: path, download: !!(opts && opts.download), name: (opts && opts.name) || undefined }); },

    // ---- crew field actions: the offline outbox (lib/outbox.ts → server/src/crewops.js) ----
    // One queued action → { status, body }. Never throws on an HTTP answer —
    // the outbox decides what each status means; no connection is status 0.
    async crewOp(e) {
      var base = window.VFP_API_BASE;
      if (!base || demoActive()) return { status: 0, body: { message: "Nothing to send to on this device." } };
      try {
        // the workspace as resolved NOW (online): one noted at a cold start
        // with no signal can be the lookup's fallback, not the real one
        await resolveWorkspace();
        var ws = WS || (e && e.ws);
        if (!ws) return { status: 0, body: { message: "Couldn't tell which workspace this is yet." } };
        var r = await svcFetch(base.replace(/\/$/, "") + "/crew/ops/" + encodeURIComponent(ws), {
          method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: JSON.stringify({ key: e.id, kind: e.kind, jobId: e.jobId, at: e.created_at, payload: e.payload || {}, queuedOffline: !!e.queuedOffline }),
        });
        var d = await r.json().catch(function () { return {}; });
        return { status: r.status, body: d };
      } catch (err) { return { status: 0, body: { message: String((err && err.message) || err) } }; }
    },
    // the GPS fence as the service checks it (radius, whether an override code
    // is needed, site pins for this login's open jobs) — cached for offline
    async crewPolicy() {
      await resolveWorkspace();
      return this.svcJson("/crew/policy/" + encodeURIComponent(WS));
    },
    // the office's answer to a crew update the service held for review
    async crewHeld(jobId, key, action) {
      await resolveWorkspace();
      return this.svcJson("/crew/held/" + encodeURIComponent(WS), "POST", { jobId: jobId, key: key, action: action });
    },
    // for Background Sync (public/outbox-sw.js): where to send, which
    // workspace, and the current short-lived access token (never the refresh token)
    async outboxAuth() {
      if (!sb || demoActive() || !window.VFP_API_BASE) return null;
      try {
        var s = await sb.auth.getSession();
        var sess = s && s.data && s.data.session;
        if (!sess) return null;
        await resolveWorkspace();
        return { apiBase: window.VFP_API_BASE, token: sess.access_token, exp: sess.expires_at || 0, ws: WS };
      } catch (e) { return null; }
    },
    // rows the outbox holds back from the diff sync until their ops have
    // landed (fn(table, id) → true), so the service sees the ops first
    holdRows: function (fn) { rowHold = typeof fn === "function" ? fn : null; },
    // website growth (migration #32): stats + checklist + Search Console + weekly report, one-click DNS
    siteGrowth() { return this.svcJson("/sites/me/growth"); },
    siteChecklistDismiss() { return this.svcJson("/sites/me/checklist/dismiss", "POST", {}); },
    siteReport(enabled) { return this.svcJson("/sites/me/report", "POST", { enabled: !!enabled }); },
    siteGscConnect() { return this.svcJson("/sites/me/gsc/connect", "POST", {}); },
    siteGscOwner(email) { return this.svcJson("/sites/me/gsc/owner", "POST", { email: email }); },
    siteDomainEntri(id) { return this.svcJson("/sites/me/domains/" + encodeURIComponent(id) + "/entri", "POST", {}); },
    // website growth II (migration #40): earned town pages, call tracking, job photos → Google
    siteEarnedCities() { return this.svcJson("/sites/me/earned-cities"); },
    siteEarnedRefresh() { return this.svcJson("/sites/me/earned-cities/refresh", "POST", {}); },
    siteEarnedAccept(id, city) { return this.svcJson("/sites/me/earned-cities/" + encodeURIComponent(id) + "/accept", "POST", { city: city || undefined }); },
    siteEarnedDismiss(id) { return this.svcJson("/sites/me/earned-cities/" + encodeURIComponent(id) + "/dismiss", "POST", {}); },
    siteCalls() { return this.svcJson("/sites/me/calls"); },
    siteCallSummary(days) { return this.svcJson("/sites/me/calls/summary?days=" + encodeURIComponent(String(days || 30))); },
    siteCallsEnable(opts) { return this.svcJson("/sites/me/calls/enable", "POST", opts || {}); },
    siteCallsSettings(patch) { return this.svcJson("/sites/me/calls/settings", "POST", patch || {}); },
    siteCallsDisable() { return this.svcJson("/sites/me/calls/disable", "POST", {}); },
    gbpPhotos() { return this.svcJson("/gbp/photos"); },
    gbpPhotoApprove(id, photos) { return this.svcJson("/gbp/photos/" + encodeURIComponent(id) + "/approve", "POST", { photos: photos || undefined }); },
    gbpPhotoSkip(id) { return this.svcJson("/gbp/photos/" + encodeURIComponent(id) + "/skip", "POST", {}); },
    gbpPhotoAuto(enabled) { return this.svcJson("/gbp/photos/auto", "POST", { enabled: !!enabled }); },

    // automations parked for one-tap "Post now"
    async socialPending() {
      var base = window.VFP_API_BASE; if (!base) return { drafts: [] };
      var r = await svcFetch(base.replace(/\/$/, "") + "/social/pending", { headers: await authHeaders() });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("social " + r.status));
      return d;
    },
    async socialPublish(eventId) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/social/publish", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ eventId: eventId }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("social publish " + r.status));
      return d;
    },
    async socialDismiss(eventId) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/social/dismiss", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ eventId: eventId }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("dismiss " + r.status));
      return d;
    },
    // create a fresh leads spreadsheet on the connected Google account
    async gsheetsCreateSheet(title) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/gsheets/create-sheet", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ title: title }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("gsheets " + r.status));
      return d;
    },
    // create a QuickBooks invoice from a quote → { invoiceId, docNumber, total, link }
    async qboCreateInvoice(quote, send) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      // the server keeps the invoice id on the SAVED quote (it's server-owned),
      // so a quote still waiting on its first save would be refused
      await resolveWorkspace();
      await saveSettled(10000);
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/quickbooks/invoice", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, quote: quote, send: !!send }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("quickbooks invoice " + r.status));
      return d;
    },
    // FW → QuickBooks: push a CHANGED quote onto its existing QBO invoice
    async qboResyncInvoice(quote) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/quickbooks/resync-invoice", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, quote: quote }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.error || ("quickbooks resync " + r.status));
      return d;
    },
    // FW → QuickBooks: reconcile a quote's paid total onto its QBO invoice
    // (idempotent on the server — records only the unsynced delta)
    async qboSyncPayments(quoteId) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/quickbooks/sync-payments", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, quoteId: quoteId }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("quickbooks sync " + r.status));
      return d;
    },
    // QuickBooks → the cash forecast, read-only: bank balance + open bills
    // → { connected, asOf, bankBalance, bankAccounts:[{name,balance}], openBills:[{id,vendor,amount,dueDate}] }
    qboCash() { return this.svcJson("/connect/quickbooks/cash"); },
    // activate the AI receptionist: provision a Retell voice agent + tracked
    // number under the platform Retell account → { agentId, number }
    // test-fire a messaging automation at the owner's own phone/email
    async automationTest(body) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/automations/test", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(body || {}),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("test " + r.status));
      return d;
    },
    // What each automation still needs before it can do anything — SMS
    // registration state plus per-automation prerequisites (financing link,
    // review URL, connected pages). The AI screen renders these as unmet
    // conditions so nothing can be switched On and quietly do nothing.
    async automationReadiness() {
      var base = window.VFP_API_BASE; if (!base) return null;
      var r = await svcFetch(base.replace(/\/$/, "") + "/automations/readiness", { headers: await authHeaders() });
      if (!r.ok) return null;
      return r.json().catch(function () { return null; });
    },
    // live talk-minutes + recent-call log straight from Retell
    async retellUsage() {
      var base = window.VFP_API_BASE; if (!base) return null;
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/retell/usage", { headers: await authHeaders() });
      if (!r.ok) return null;
      return r.json().catch(function () { return null; });
    },
    // push business knowledge (pricing/FAQ) into the live receptionist's prompt
    async retellSyncKnowledge(knowledge) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/retell/sync-knowledge", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, knowledge: knowledge }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("retell sync " + r.status));
      return d;
    },
    // a second service's own AI line (ai_lines; §13 item 25): the rows, add one, release its number
    async aiLines() {
      if (!sb) return [];
      var r = await sb.from("ai_lines").select("*").eq("workspace_id", WS);
      return r.error ? [] : (r.data || []).filter(function (l) { return l && l.vertical !== "fence"; });
    },
    async retellProvisionLine(opts) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/retell/provision-line", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(Object.assign({ workspaceId: WS }, opts || {})),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("retell provision-line " + r.status));
      return d;
    },
    async retellReleaseLine(vertical) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/retell/release-line", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ workspaceId: WS, vertical: vertical }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("retell release-line " + r.status));
      return d;
    },
    // a brand's own Google rating (brand_ratings; §13 item 25): read it, fetch it from a place id / Maps link / Business Profile location, clear it
    async brandRating(vertical) {
      if (!sb) return null;
      var r = await sb.from("brand_ratings").select("*").eq("workspace_id", WS).eq("vertical", vertical).maybeSingle();
      return r.error ? null : (r.data || null);
    },
    async brandRatingRefresh(opts) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/brand-rating", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(Object.assign({ workspaceId: WS }, opts || {})),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("brand rating " + r.status));
      return d;
    },
    async retellProvision(opts) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/connect/retell/provision", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify(Object.assign({ workspaceId: WS }, opts || {})),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.error || ("retell provision " + r.status));
      return d;
    },
    // fire an app event into the automation service (lead.created, job.booked, …)
    async emitEvent(trigger, payload) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/events/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify({ trigger: trigger, payload: payload || {} }),
      });
      if (!r.ok) throw new Error("event " + trigger + " → " + r.status);
      return r.json();
    },

    // ---------- team: multi-seat workspaces ----------
    async teamCall(path, body, method) {
      var base = window.VFP_API_BASE; if (!base) return { ok: false, skipped: true };
      var r = await svcFetch(base.replace(/\/$/, "") + "/team" + path, {
        method: method || "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: method === "GET" ? undefined : JSON.stringify(body || {}),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("team " + r.status));
      return d;
    },
    teamMembers() { return this.teamCall("/members", null, "GET"); },
    teamInvite(email, role) { return this.teamCall("/invite", { email: email, role: role }); },
    teamAccept(token) { return this.teamCall("/accept", { token: token }); },
    // PUBLIC lookup so the invite landing can greet the invitee pre-auth
    async inviteInfo(token) {
      var base = window.VFP_API_BASE; if (!base || !token) return null;
      try {
        var r = await fetch(base.replace(/\/$/, "") + "/team/invite/" + encodeURIComponent(token));
        if (!r.ok) return null;
        return await r.json();
      } catch (e) { return null; }
    },
    teamRemove(target) { return this.teamCall("/remove", target); },

    // ---------- chat: conversations + messages (customer hub threads) ----------
    // Hub messages persist server-side (conversations/messages); under RLS the
    // signed-in contractor reads/writes their own workspace's threads directly.
    chat: {
      async list() {
        if (!sb || demoActive()) return [];
        await resolveWorkspace();
        var r = await sb.from("conversations").select("*").eq("workspace_id", WS).order("last_at", { ascending: false }).limit(200);
        if (r.error) throw r.error;
        return (r && r.data) || [];
      },
      async messages(conversationId) {
        if (!sb || demoActive()) return [];
        var r = await sb.from("messages").select("id, role, body, channel, created_at, data").eq("conversation_id", conversationId).order("created_at", { ascending: true }).limit(500);
        if (r.error) throw r.error;
        return ((r && r.data) || []).map(function (m) { return Object.assign({}, m, { image: m.data && m.data.image }); });
      },
      async send(conversationId, body, image) {
        if (!sb || demoActive()) throw new Error("Offline");
        await resolveWorkspace();
        var ins = await sb.from("messages").insert({ workspace_id: WS, conversation_id: conversationId, role: "company", channel: "hub", body: body || "", data: image ? { image: image } : {} });
        if (ins.error) throw ins.error;
        var cur = await sb.from("conversations").select("data").eq("id", conversationId).maybeSingle();
        if (cur.error) throw cur.error;
        var d = Object.assign({}, (cur && cur.data && cur.data.data) || {}, { last_role: "company", contractor_read_at: new Date().toISOString() });
        var up = await sb.from("conversations").update({ last_at: new Date().toISOString(), data: d }).eq("id", conversationId);
        if (up.error) throw up.error;
        return { ok: true };
      },
      // unread = the customer spoke last and we haven't opened the thread since
      async unreadInfo() {
        var rows = await this.list();
        var convos = [], customers = [];
        rows.forEach(function (r) {
          var d = (r && r.data) || {};
          var un = d.last_role === "customer" && (!d.contractor_read_at || (r.last_at && r.last_at > d.contractor_read_at));
          if (un) { convos.push(r.id); if (r.customer_id && customers.indexOf(r.customer_id) === -1) customers.push(r.customer_id); }
        });
        // count = distinct threads (a customer with two legacy convo rows is ONE unread)
        return { count: customers.length || convos.length, convos: convos, customers: customers };
      },
      async markRead(conversationId) {
        if (!sb || !conversationId || demoActive()) return;
        var cur = await sb.from("conversations").select("data").eq("id", conversationId).maybeSingle();
        if (cur.error) throw cur.error;
        var d = Object.assign({}, (cur && cur.data && cur.data.data) || {}, { contractor_read_at: new Date().toISOString() });
        var up = await sb.from("conversations").update({ data: d }).eq("id", conversationId);
        if (up.error) throw up.error;
      },
      // find (or start) the thread for a customer record
      async ensureForCustomer(customer) {
        if (!sb || demoActive()) throw new Error("Offline");
        await resolveWorkspace();
        var f = await sb.from("conversations").select("*").eq("workspace_id", WS).eq("customer_id", customer.id).order("last_at", { ascending: false }).limit(1);
        if (f && f.data && f.data[0]) return f.data[0];
        var row = {
          id: "conv-" + Date.now() + "-" + Math.floor(Math.random() * 1e4),
          workspace_id: WS, customer_id: customer.id, channel: "hub",
          phone: customer.phone || null, email: customer.email || null,
          last_at: new Date().toISOString(),
        };
        var ins = await sb.from("conversations").insert(row);
        if (ins.error) throw ins.error;
        return row;
      },
    },

    // ---------- quote sending (estimate email via the service) ----------
    async sendQuoteEmail(quoteId) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Sending isn't configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/payments/send-quote/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify({ quoteId: quoteId }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("send " + r.status));
      return d;
    },

    // ---------- in person: the customer signs on the contractor's device ----------
    // Same contract, hash and audit trail as the hub; the server records that it
    // happened in person and which signed-in teammate presented the device.
    // → { ok, already?, signedAt, docHash, total, quote }
    signInPerson(body) { return inPersonCall("/sign", body); },
    // → { ok, already?, approvedAt, revisedTotal, quote }
    approveChangeOrderInPerson(body) { return inPersonCall("/approve-co", body); },

    /* "Regenerate link" / "Fix link": the service writes this one customer row
       (and the token) directly, so the hub link resolves without waiting on
       the whole-state sync. Returns { token, url, created, reissued }. */
    async hubLink(customer, opts) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Fixing links needs the online service");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/payments/hub-link/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ customer: customer, regenerate: !!(opts && opts.regenerate) }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) { var e = new Error(d.message || d.error || ("hub link " + r.status)); e.code = d.error || null; throw e; }
      return d;
    },

    // ---------- print kit (headless-Chrome PDFs + the print order) ----------
    async printPdf(body) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Print-ready PDFs need the online service.");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/print/pdf/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify(body || {}),
      });
      if (!r.ok) { var d = await r.json().catch(function () { return {}; }); throw new Error(d.message || d.error || ("pdf " + r.status)); }
      return r.blob();
    },
    // print orders: rows in print_orders, files in the print-files bucket,
    // payment through Stripe Checkout — all via the service (service-role tables)
    print: {
      enabled: function () { return !!window.VFP_API_BASE && !demoActive(); },
      async call(path, body, method) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("Print orders need the online service (app.fenceworks.ai)");
        await resolveWorkspace();
        var r = await svcFetch(base.replace(/\/$/, "") + "/print" + path.replace("{ws}", encodeURIComponent(WS)), {
          method: method || (body ? "POST" : "GET"),
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) { var e = new Error(d.message || d.error || ("print " + r.status)); e.status = r.status; throw e; }
        return d;
      },
      health() { var base = window.VFP_API_BASE; if (!base) return Promise.resolve(null); return fetch(base.replace(/\/$/, "") + "/print/health").then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }); },
      order(payload) { return this.call("/order/{ws}", payload); },
      orders() { return this.call("/orders/{ws}").then(function (d) { return d.orders || []; }); },
      pay(orderId) { return this.call("/orders/{ws}/" + encodeURIComponent(orderId) + "/pay", {}); },
      fileUrl(orderId, i) { var base = window.VFP_API_BASE; return base ? base.replace(/\/$/, "") + "/print/orders/" + encodeURIComponent(WS) + "/" + encodeURIComponent(orderId) + "/file/" + i : null; },
      // the price book, prices only (apparel per piece at each break) — cart estimates
      prices() { return this.call("/prices/{ws}").then(function (d) { return d.prices || []; }); },
      admin: {
        orders(status) { return window.VFP_DB.print.call("/admin/orders" + (status ? "?status=" + encodeURIComponent(status) : "")); },
        order(id) { return window.VFP_DB.print.call("/admin/orders/" + encodeURIComponent(id)); },
        update(id, patch) { return window.VFP_DB.print.call("/admin/orders/" + encodeURIComponent(id), patch, "PATCH"); },
        submit(id, provider) { return window.VFP_DB.print.call("/admin/orders/" + encodeURIComponent(id) + "/submit", { provider: provider }); },
        render(id) { return window.VFP_DB.print.call("/admin/orders/" + encodeURIComponent(id) + "/render", {}); },
        products() { return window.VFP_DB.print.call("/admin/products"); },
        saveProducts(rows) { return window.VFP_DB.print.call("/admin/products", { rows: rows }, "PUT"); },
        // the buyer's sheet a supplier gets (html + text), and re-sending every supplier's hand-off
        sheet(id, provider) { return window.VFP_DB.print.call("/admin/orders/" + encodeURIComponent(id) + "/sheet" + (provider ? "?provider=" + encodeURIComponent(provider) : "")); },
        resend(id, provider) { return window.VFP_DB.print.call("/admin/orders/" + encodeURIComponent(id) + "/submit", { provider: provider, resend: true }); },
      },
    },

    // ---------- consumer financing (per-contractor Wisetack) ----------
    async financingActivity() {
      if (!sb || demoActive()) return [];
      await resolveWorkspace();
      var r = await sb.from("events").select("type, outcome, payload, created_at").eq("workspace_id", WS)
        .like("type", "financing.%").order("created_at", { ascending: false }).limit(8);
      return (r && r.data) || [];
    },
    async financingLink(quoteId) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Financing isn't configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/payments/financing-link/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify({ quoteId: quoteId }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) { var e = new Error(d.message || d.error || ("financing " + r.status)); e.code = d.error; throw e; }
      return d; // { url, transactionId }
    },

    // ---------- billing history (real Stripe invoices) ----------
    async billingInvoices() {
      var base = window.VFP_API_BASE; if (!base) return { invoices: [] };
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/invoices/" + encodeURIComponent(WS), { headers: await authHeaders() });
      if (!r.ok) throw new Error("invoices " + r.status);
      return r.json();
    },

    // ---------- usage & credits (server is the meter of record) ----------
    // The authoritative usage snapshot the Account page meters read. Totals are
    // already TRIAL-CAPPED server-side, so a trial reads e.g. 0/100, consistent
    // with enforcement. Shape: { credits:{used,total}, voiceMin:{used,total}, balance }.
    async billingUsage() {
      var base = window.VFP_API_BASE; if (!base) return null;
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/usage/" + encodeURIComponent(WS), { headers: await authHeaders() });
      return r.ok ? r.json() : null;
    },

    // ---------- AI fence rendering (visualizer) ----------
    async renderQuota() {
      var base = window.VFP_API_BASE; if (!base) return null;
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/render/quota/" + encodeURIComponent(WS), { headers: await authHeaders() });
      return r.ok ? r.json() : null;
    },
    async renderFence(payload) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("AI rendering needs the live service");
      await resolveWorkspace();
      // image generation runs ~30-90s; without a deadline a hung request
      // leaves the Visualizer spinning forever
      var ctl = new AbortController();
      var timer = setTimeout(function () { ctl.abort(); }, 150000);
      var r;
      try {
        r = await svcFetch(base.replace(/\/$/, "") + "/render/fence", {
          method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: JSON.stringify(Object.assign({ workspaceId: WS }, payload || {})),
          signal: ctl.signal,
        });
      } catch (e) {
        if (ctl.signal.aborted) throw new Error("The render took too long (2½ min) and was cancelled — try again, or use a smaller photo.");
        throw e;
      } finally { clearTimeout(timer); }
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("render " + r.status));
      return d;
    },

    // ---------- billing (Stripe subscriptions) ----------
    // Each call resolves the caller's workspace FIRST. Otherwise these can run
    // before load() has resolved WS (the checkout effect races the data load),
    // posting against the default "fenceworks" workspace — which is seeded
    // active/free, so status looks "subscribed" and checkout gets skipped.
    async billingCheckout(plan) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Billing service not configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/checkout/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify({ plan: plan }),
      });
      if (!r.ok) throw await billingError(r, "checkout");
      return r.json(); // { url } for a new subscription, { updated, plan, direction, seats } for a switch
    },
    async billingPortal() {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Billing service not configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/portal/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: "{}",
      });
      if (!r.ok) throw new Error("portal " + r.status + ": " + (await r.text()));
      return r.json(); // { url }
    },
    // extend an in-progress trial by 14 days (owner-only, one-time). The server
    // pushes the new trial_end to Stripe, re-saves the row, and returns the new
    // end date. Used by the trial-aware cancel flow's "Need more time?" offer.
    async billingExtendTrial() {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Billing service not configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/extend-trial/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: "{}",
      });
      if (!r.ok) throw new Error("extend-trial " + r.status + ": " + (await r.text()));
      return r.json(); // { ok, trial_ends_at }
    },
    async billingStatus() {
      var base = window.VFP_API_BASE; if (!base) return { subscription: null };
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/status/" + encodeURIComponent(WS), { headers: await authHeaders() });
      if (!r.ok) throw new Error("billing " + r.status);
      return r.json();
    },
    // set the workspace's TOTAL seat count; the server bills the seats beyond
    // the plan's included count at the per-seat ($5/mo) price.
    async billingUpdateSeats(total) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Billing service not configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/seats/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify({ total: total }),
      });
      if (!r.ok) throw await billingError(r, "seats");
      return r.json(); // { ok, seats, extra, included }
    },
    // buy a one-time credit pack (pack = "pack500" | "pack2k" | "pack5k"); the
    // server starts a Stripe Checkout charge and the webhook credits the balance.
    async billingBuyCredits(pack) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Billing service not configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/credits/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify({ pack: pack }),
      });
      if (!r.ok) throw new Error("credits " + r.status + ": " + (await r.text()));
      return r.json(); // { url }
    },
    // confirm a credit-pack purchase on the success redirect — the server verifies
    // the Stripe Checkout session and credits the balance (idempotent), so the
    // top-up lands even when the webhook isn't configured / delivered (test mode).
    async billingCreditsConfirm(sessionId) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Billing service not configured");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/billing/credits/confirm/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify({ session_id: sessionId }),
      });
      if (!r.ok) throw new Error("credits confirm " + r.status + ": " + (await r.text()));
      return r.json(); // { ok, credited, balance }
    },
    // Submit the cancel flow's branched survey (reason → follow-up → detail) to
    // the central, anonymized cancellation_feedback table. Fired fire-and-forget
    // on the TERMINAL action (cancel / keep / extend trial / downgrade), so it
    // must NEVER block the UI: no live service or any failure just returns null.
    // payload: { reason, followup, detail, plan, wasTrial, outcome }.
    async cancelFeedback(payload) {
      var base = window.VFP_API_BASE; if (!base) return null;
      try {
        await resolveWorkspace();
        var r = await svcFetch(base.replace(/\/$/, "") + "/billing/cancel-feedback/" + encodeURIComponent(WS), {
          method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify(payload || {}),
        });
        return r.ok ? r.json().catch(function () { return null; }) : null;
      } catch (e) { return null; }
    },

    /* ---------- account: deletion, as a REQUEST (GDPR/CCPA "delete on request") ----
       FW-029: there used to be a deleteAccount() here that POSTed to a route
       which wiped the workspace synchronously. It wiped the identifiers before
       anything released the resources — the Twilio number stayed rented, the
       A2P campaign kept billing, the Retell agent kept its number, with the row
       naming every SID gone — and it was one request away from unrecoverable on
       a product where the data is the business.

       The owner asks; Fence Works reviews it and runs the purge, which releases
       the paid provider resources first. Either side can cancel while it's
       open. */
    async accountDeletionRequest() {
      var base = window.VFP_API_BASE; if (!base) return { request: null, setup: false };
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/account/delete-request", { headers: await authHeaders() });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("deletion " + r.status));
      return d; // { request, setup }
    },
    async requestAccountDeletion(reason) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Account deletion needs the live service");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/account/delete-request", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
        body: JSON.stringify({ reason: reason || "" }),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("deletion " + r.status));
      return d; // { ok, request, already? }
    },
    async cancelAccountDeletion() {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Account deletion needs the live service");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/account/delete-request/cancel", {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: "{}",
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("cancel " + r.status));
      return d;
    },

    // ---------- SMS: per-contractor A2P 10DLC registration (business texting) ----------
    // smsRegister submits the contractor's A2P business profile; the server (owner-
    // only) provisions a Brand + us_app_to_person Campaign + Messaging Service +
    // 10DLC number and returns the status view (pending while carriers review).
    // smsStatus reads (and, while pending, refreshes) that registration state.
    // Both resolve the caller's OWN workspace first. No live service → throw /
    // return null, consistent with the other service methods.
    async smsRegister(profile) {
      var base = window.VFP_API_BASE; if (!base) throw new Error("Business texting setup needs the live service");
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/sms/register/" + encodeURIComponent(WS), {
        method: "POST", headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()), body: JSON.stringify(profile || {}),
      });
      var d = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error(d.message || d.error || ("sms register " + r.status));
      return d; // { status, number, brandSid, campaignSid, messagingServiceSid, ... }
    },
    async smsStatus() {
      var base = window.VFP_API_BASE; if (!base) return null;
      await resolveWorkspace();
      var r = await svcFetch(base.replace(/\/$/, "") + "/sms/status/" + encodeURIComponent(WS), { headers: await authHeaders() });
      return r.ok ? r.json().catch(function () { return null; }) : null;
    },
    // ---------- help chat (the "?" bubble → the Fence Works founder's inbox) ----------
    // All through the service (server/src/support.js): the tables are
    // service-role-only, the server identifies the caller from the session
    // and notifies the owner. No live service (local/demo) → null / throw,
    // and the bubble falls back to a mailto: link.
    support: {
      enabled: function () { return !!window.VFP_API_BASE; },
      async call(path, body, method) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("Help chat needs the live service (app.fenceworks.ai)");
        var r = await svcFetch(base.replace(/\/$/, "") + "/support" + path, {
          method: method || (body ? "POST" : "GET"),
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) throw new Error(d.message || d.error || ("support " + r.status));
        return d;
      },
      me() { return window.VFP_API_BASE ? this.call("/me").catch(function () { return null; }) : Promise.resolve(null); },
      send(body, page) { return this.call("/send", { body: body, page: page || "" }); },
      read() { return this.call("/read", {}).catch(function () {}); },
      inbox() { return this.call("/inbox"); },
      thread(id) { return this.call("/thread/" + encodeURIComponent(id)); },
      reply(threadId, body) { return this.call("/reply", { threadId: threadId, body: body }); },
      setStatus(threadId, status) { return this.call("/status", { threadId: threadId, status: status }); },
    },

    /* FW-030/031 — ops. Platform owner only; the server 403s anyone else.
       `diagnostics` answers "what is running and is it whole"; `recovery`
       answers "what broke and nobody looked". A dead-lettered send used to be
       a row nobody could see, let alone retry. */
    ops: {
      enabled: function () { return !!window.VFP_API_BASE; },
      async call(path, body, method) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("Ops needs the live service");
        var r = await svcFetch(base.replace(/\/$/, "") + path, {
          method: method || (body ? "POST" : "GET"),
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) throw new Error(d.message || d.error || ("ops " + r.status));
        return d;
      },
      diagnostics() { return this.call("/diagnostics"); },
      capacity(hours) { return this.call("/ops/capacity?hours=" + (hours === 168 ? 168 : 24)); },   // Ops & Health → Capacity: 24 hours or 7 days
      recovery(limit) { return this.call("/ops/recovery?limit=" + (limit || 50)); },
      // "why did this connect fail" — Stripe Connect state, SERVICE_URL, the
      // exact redirect_uri per provider, missing env, and the last recorded
      // failure in the provider's own words. Owner-only.
      connectReadiness() { return this.call("/ops/connect-readiness"); },
      stripeReadiness() { return this.call("/ops/stripe-readiness"); },
      retryDeadLetter(id) { return this.call("/ops/dead-letters/" + encodeURIComponent(id) + "/retry", {}); },
      dismissDeadLetter(id, note) { return this.call("/ops/dead-letters/" + encodeURIComponent(id) + "/dismiss", { note: note || "" }); },
      reapplyPayment(id) { return this.call("/ops/payments/" + encodeURIComponent(id) + "/reapply", {}); },
      // FW Admin → Phone lines: every tenant's business lines (voice/routes.js fwLines)
      lines() { return this.call("/fwadmin/lines"); },
      lineBuy(ws, opts) { return this.call("/fwadmin/lines/buy", Object.assign({ ws: ws }, opts || {})); },
      lineAdopt(ws, opts) { return this.call("/fwadmin/lines/adopt/" + encodeURIComponent(ws), opts || {}); },
      lineRouting(id, routing) { return this.call("/fwadmin/lines/" + encodeURIComponent(id) + "/routing", { routing: routing }, "PUT"); },
      lineRelease(id) { return this.call("/fwadmin/lines/" + encodeURIComponent(id) + "/release", {}); },
      // a QuickBooks change (usually a payment recorded in QBO) that gave up
      retryQboEvent(id) { return this.call("/ops/qbo-events/" + encodeURIComponent(id) + "/retry", {}); },
      dismissQboEvent(id, note) { return this.call("/ops/qbo-events/" + encodeURIComponent(id) + "/dismiss", { note: note || "" }); },
      deletions(status) { return this.call("/account/deletions" + (status ? "?status=" + encodeURIComponent(status) : "")); },
      purgeWorkspace(requestId, confirm) { return this.call("/account/deletions/" + encodeURIComponent(requestId) + "/purge", { confirm: confirm }); },
      declineDeletion(requestId, note) { return this.call("/account/deletions/" + encodeURIComponent(requestId) + "/decline", { note: note || "" }); },
      // FW Admin → Customers / Lifecycle Emails / Growth (server/src/fwcustomers.js)
      customers() { return this.call("/fwadmin/customers"); },
      customer(ws) { return this.call("/fwadmin/customers/" + encodeURIComponent(ws)); },
      updateCustomer(ws, patch) { return this.call("/fwadmin/customers/" + encodeURIComponent(ws), patch, "PATCH"); },
      // "Comp this account" (plan_comps, #38): { plan, until: "YYYY-MM-DD" | "", note } — starts one, or changes the one in force
      compAccount(ws, comp) { return this.call("/fwadmin/customers/" + encodeURIComponent(ws) + "/comp", comp || {}); },
      endComp(ws) { return this.call("/fwadmin/customers/" + encodeURIComponent(ws) + "/comp/end", {}); },
      // Services (the switcher, migration #43; server/src/verticalsapi.js): which services a workspace runs — read, and set
      verticals(ws) { return this.call("/fwadmin/customers/" + encodeURIComponent(ws) + "/verticals"); },
      setVerticals(ws, verticals) { return this.call("/fwadmin/customers/" + encodeURIComponent(ws) + "/verticals", { verticals: verticals }); },
      sendLifecycle(ws, step) { return this.call("/fwadmin/customers/" + encodeURIComponent(ws) + "/send", { step: step }); },
      syncBilling(ws) { return this.call(ws ? "/fwadmin/customers/" + encodeURIComponent(ws) + "/sync-billing" : "/fwadmin/sync-billing", {}); },
      lifecycle() { return this.call("/fwadmin/lifecycle"); },
      lifecyclePreview(step, ws) { return this.call("/fwadmin/lifecycle/preview/" + encodeURIComponent(step) + (ws ? "?ws=" + encodeURIComponent(ws) : "")); },
      // who one email went to, and when (sent / opened / clicked timestamps)
      stepSends(step) { return this.call("/fwadmin/lifecycle/steps/" + encodeURIComponent(step) + "/sends"); },
      growth() { return this.call("/fwadmin/growth"); },
      template(step) { return this.call("/fwadmin/lifecycle/templates/" + encodeURIComponent(step)); },
      saveTemplate(step, t) { return this.call("/fwadmin/lifecycle/templates/" + encodeURIComponent(step), t, "PUT"); },
      toggleTemplate(step, enabled) { return this.call("/fwadmin/lifecycle/templates/" + encodeURIComponent(step), { enabled: !!enabled }, "PATCH"); },
      resetTemplate(step) { return this.call("/fwadmin/lifecycle/templates/" + encodeURIComponent(step), null, "DELETE"); },
      previewDraft(step, draft, ws) { return this.call("/fwadmin/lifecycle/preview/" + encodeURIComponent(step), { draft: draft, ws: ws || null }); },
      uploadEmailImage(dataUrl) { return this.call("/fwadmin/lifecycle/images", { data: dataUrl }); },
      emailImages() { return this.call("/fwadmin/lifecycle/images"); },
    },
    /* FW Admin → Lead Engine (docs/LEAD_ENGINE_PLAN.md §14.3): the CRM
       (server/src/leadengine/crm.js), find & enrich (enrich.js) and the
       preview sites (pitchsite.js), all under /leadengine. Platform owner
       only; the server 403s anyone else. A refused call throws an Error
       carrying the server's `error` code as .code (e.g. "migration" before
       migrate_2026-10-03_lead_engine.sql has run, "places_key_missing",
       "not_enriched") and the HTTP status as .status. */
    leadengine: {
      enabled: function () { return !!window.VFP_API_BASE; },
      async call(path, body, method) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("The Lead Engine needs the live service");
        var r = await svcFetch(base.replace(/\/$/, "") + "/leadengine" + path, {
          method: method || (body ? "POST" : "GET"),
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) {
          var e = new Error(d.message || d.error || ("leadengine " + r.status));
          e.code = d.error || null; e.status = r.status; e.detail = d;
          throw e;
        }
        return d;
      },
      list(q) {
        var qs = [];
        Object.keys(q || {}).forEach(function (k) { if (q[k] != null && q[k] !== "") qs.push(encodeURIComponent(k) + "=" + encodeURIComponent(String(q[k]))); });
        return this.call("/prospects" + (qs.length ? "?" + qs.join("&") : ""));
      },
      get(id) { return this.call("/prospects/" + encodeURIComponent(id)); },
      create(p) { return this.call("/prospects", p || {}); },
      update(id, patch) { return this.call("/prospects/" + encodeURIComponent(id), patch || {}); },
      log(id, entry) { return this.call("/prospects/" + encodeURIComponent(id) + "/log", entry || {}); },
      remove(id) { return this.call("/prospects/" + encodeURIComponent(id) + "/delete", {}); },
      // opts: { defaults ("Apply to every row"), dryRun, includeDeleted }
      importRows(rows, source, file, opts) { return this.call("/import", Object.assign({ rows: rows || [], source: source || "csv", file: file || null }, opts || {})); },
      // { ids } or { filter: the list's own filters }, action: "delete" | "stage" | "tag"
      bulk(body) { return this.call("/prospects/bulk", body || {}); },
      today() { return this.call("/today"); },
      // B — find & enrich
      enrich(id) { return this.call("/prospects/" + encodeURIComponent(id) + "/enrich", {}); },
      addLink(url) { return this.call("/add-link", { url: url }); },
      find(q) { return this.call("/find", q || {}); },
      findAdd(placeIds) { return this.call("/find/add", { place_ids: placeIds || [] }); },
      // C — preview sites
      buildSite(id, answers) { return this.call("/prospects/" + encodeURIComponent(id) + "/site", answers ? { answers: answers } : {}); },
      extendSite(id, days) { return this.call("/prospects/" + encodeURIComponent(id) + "/site/extend", { days: days || 30 }); },
      deleteSite(id) { return this.call("/prospects/" + encodeURIComponent(id) + "/site/delete", {}); },
    },
    /* FW Admin → Admins (server/src/platformadminsapi.js): who else is a
       platform admin. Platform admins only; the server 403s anyone else and
       answers 503 { error: "migration" } until migration #33 has run. */
    platformAdmins: {
      enabled: function () { return !!window.VFP_API_BASE; },
      async call(path, body) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("Admins need the live service");
        var r = await svcFetch(base.replace(/\/$/, "") + "/platform-admins" + path, {
          method: body ? "POST" : "GET",
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) {
          var e = new Error(d.message || d.error || ("admins " + r.status));
          e.code = d.error || null; e.status = r.status;
          throw e;
        }
        return d;
      },
      list() { return this.call(""); },
      add(email) { return this.call("", { email: email }); },
      remove(email) { return this.call("/remove", { email: email }); },
    },
    /* phone + desktop notifications (server/src/push.js, web/src/lib/push.ts).
       503 { error: "push_off" } without VAPID keys on the server, 503
       { error: "migration" } before migration #37. Not in the sandbox. */
    push: {
      enabled: function () { return !!window.VFP_API_BASE && !demoActive(); },
      async call(path, body) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("Notifications need the live service");
        if (demoActive()) { var e0 = new Error("The sandbox doesn't send notifications."); e0.code = "demo"; throw e0; }
        var r = await svcFetch(base.replace(/\/$/, "") + "/push" + path, {
          method: body ? "POST" : "GET",
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) { var e = new Error(d.message || d.error || ("notifications " + r.status)); e.code = d.error || null; e.status = r.status; throw e; }
        return d;
      },
      config() { return this.call("/config"); },
      subscribe(subscription, device) { return this.call("/subscribe", { subscription: subscription, device: device || undefined }); },
      unsubscribe(endpoint) { return this.call("/unsubscribe", { endpoint: endpoint }); },
      devices() { return this.call("/devices"); },
      removeDevice(id) { return this.call("/devices/" + encodeURIComponent(id) + "/remove", {}); },
      test() { return this.call("/test", {}); },
      prefs() { return this.call("/preferences"); },
      setPrefs(events) { return this.call("/preferences", { events: events }); },
    },
    /* FW Admin → Status & To-do (server/src/platformstatusapi.js): readiness
       (names and on/off only), migrations, and the owner's Waiting on and
       To-do lists. Platform admins only; writes answer 503 { error:
       "migration" } until migration #35 has run. */
    platformStatus: {
      enabled: function () { return !!window.VFP_API_BASE; },
      async call(path, body) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("Status & To-do needs the live service");
        var r = await svcFetch(base.replace(/\/$/, "") + "/platform-status" + path, {
          method: body ? "POST" : "GET",
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) {
          var e = new Error(d.message || d.error || ("status " + r.status));
          e.code = d.error || null; e.status = r.status; e.field = d.field || null;
          throw e;
        }
        return d;
      },
      load(fresh) { return this.call(fresh ? "?fresh=1" : ""); },
      create(item) { return this.call("/items", item); },
      update(id, patch) { return this.call("/items/" + encodeURIComponent(id), patch); },
      remove(id) { return this.call("/items/" + encodeURIComponent(id) + "/delete", {}); },
      reorder(kind, ids) { return this.call("/reorder", { kind: kind, ids: ids }); },
      seed() { return this.call("/seed", {}); },
    },
    /* in-app activity → FW Admin customer timeline (server/src/activity.js).
       Plain fetch, not svcFetch: a tracking call must never flip the
       "signed out" pill or retry noisily. keepalive lets the last batch
       leave while the tab closes. Not while the sandbox is active. */
    track: {
      async send(events) {
        var base = window.VFP_API_BASE;
        if (!base || !events || !events.length || (window.VFP_DEMO && window.VFP_DEMO.active)) return false;
        var h = await authHeaders();
        if (!h.Authorization) return false;
        try {
          var r = await fetch(base.replace(/\/$/, "") + "/track", { method: "POST", keepalive: true, headers: Object.assign({ "Content-Type": "application/json" }, h), body: JSON.stringify({ events: events }) });
          return r.ok || r.status === 202;
        } catch (e) { return false; }
      },
    },
    /* crashes → FW Admin → Ops & Health → App errors (lib/crash.ts →
       server/src/clienterrors.js, migration #38). report() is plain fetch
       like track: never the "signed out" pill, never a retry. It goes signed
       out too (a crash on the sign-in screen or a hub page) — the server
       takes the account from the session, never from the report. Not in the
       sandbox. list / setStatus are the platform admins' (403 anyone else). */
    clientErrors: {
      async report(payload) {
        var base = window.VFP_API_BASE;
        if (!base || !payload || demoActive()) return false;
        var h = {};
        try { h = await authHeaders(); } catch (e) {}
        try {
          var r = await fetch(base.replace(/\/$/, "") + "/client-errors", { method: "POST", keepalive: true, headers: Object.assign({ "Content-Type": "application/json" }, h), body: JSON.stringify(payload) });
          return r.ok || r.status === 202 || r.status === 204;
        } catch (e) { return false; }
      },
      async call(path, body) {
        var base = window.VFP_API_BASE; if (!base) throw new Error("App errors need the live service");
        var r = await svcFetch(base.replace(/\/$/, "") + "/client-errors" + path, {
          method: body ? "POST" : "GET",
          headers: Object.assign({ "Content-Type": "application/json" }, await authHeaders()),
          body: body ? JSON.stringify(body) : undefined,
        });
        var d = await r.json().catch(function () { return {}; });
        if (!r.ok) { var e = new Error(d.message || d.error || ("app errors " + r.status)); e.code = d.error || null; e.status = r.status; throw e; }
        return d;
      },
      list(status) { return this.call("?status=" + encodeURIComponent(status || "open")); },
      setStatus(fingerprint, status) { return this.call("/" + encodeURIComponent(fingerprint) + "/status", { status: status }); },
    },
    onStatus: function (fn) { listeners.push(fn); return function () { listeners = listeners.filter(function (x) { return x !== fn; }); }; },

    // ---------- AUTH (Supabase email + password) ----------
    auth: {
      // the sandbox renders as signed-out (no auth gate, no per-user reads);
      // signUp stays callable so "Keep this as my free workspace" can create
      // the real account from inside it.
      enabled: ready && !demoActive(),
      async getUser() {
        if (!sb || demoActive()) return null;
        try {
          var r = await sb.auth.getSession();
          var sess = r && r.data && r.data.session;
          if (!sess) return null;
          var u = sess.user;
          var name = (u.user_metadata && (u.user_metadata.full_name || u.user_metadata.name)) || (u.email ? u.email.split("@")[0] : "User");
          return { id: u.id, email: u.email, name: name };
        } catch (e) { return null; }
      },
      async signIn(email, password) {
        if (!sb) throw new Error("Auth unavailable");
        var r = await sb.auth.signInWithPassword({ email: email, password: password });
        if (r.error) throw r.error;
        return r.data.user;
      },
      async signUp(email, password, fullName, company, attribution, opts) {
        if (!sb) throw new Error("Auth unavailable");
        // company → workspace name (the signup trigger spins up a new workspace per user)
        // Attribution rides in the SAME user metadata: handle_new_user() reads
        // heard_about / heard_detail / attribution out of raw_user_meta_data and
        // writes them onto the workspace row. Doing it here (rather than a
        // follow-up update) means it lands atomically with account creation —
        // an account can never exist without the source that produced it.
        var meta = { full_name: fullName || "", company: company || "" };
        if (attribution) {
          if (attribution.heard_about) meta.heard_about = String(attribution.heard_about).slice(0, 40);
          if (attribution.heard_detail) meta.heard_detail = String(attribution.heard_detail).slice(0, 160);
          if (attribution.attribution && typeof attribution.attribution === "object") meta.attribution = attribution.attribution;
        }
        // a teammate joining through an invite, not a new customer: the
        // service skips the new-account alert for it (server/src/auth.js)
        if (opts && opts.teamInvite) meta.team_invite = true;
        var r = await sb.auth.signUp({ email: email, password: password, options: { data: meta } });
        if (r.error) throw r.error;
        return r.data;
      },
      async signOut() { if (sb) await sb.auth.signOut(); },
      // forgot-password: email a recovery link; the link signs the user in and
      // app.tsx flags fw_pw_recovery so the app prompts for the new password
      async resetPassword(email) {
        if (!sb) throw new Error("Auth unavailable");
        var r = await sb.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin + "/" });
        if (r.error) throw r.error;
        return true;
      },
      async updatePassword(password) {
        if (!sb) throw new Error("Auth unavailable");
        var r = await sb.auth.updateUser({ password: password });
        if (r.error) throw r.error;
        return true;
      },
      onChange(fn) {
        if (!sb) return function () {};
        var sub = sb.auth.onAuthStateChange(function (_e, session) {
          if (!session) return fn(null);
          var u = session.user;
          var name = (u.user_metadata && (u.user_metadata.full_name || u.user_metadata.name)) || (u.email ? u.email.split("@")[0] : "User");
          fn({ id: u.id, email: u.email, name: name });
        });
        return function () { sub && sub.data && sub.data.subscription && sub.data.subscription.unsubscribe(); };
      },
    },
  };
})();
